import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowLeft, Sparkles, Upload, X, Download, Loader2, Wand2, Film, Clapperboard,
  Coins, Plus, Play, RefreshCw, Layers, MessageSquarePlus, Video, Music2, ImagePlus,
  Pencil, Check, ChevronDown, Sparkle, Lock, SlidersHorizontal, MessageSquare, Paperclip, Zap,
  BarChart3, Users, Trash2, AlertTriangle, Megaphone,
} from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../../config';
import VideoTrimModal from './VideoTrimModal';
import AudioTrimModal from './AudioTrimModal';
import StripePaymentForm from './StripePaymentForm';
import MyConsumptionStatsModal from './MyConsumptionStatsModal';
import FilmFlow from './FilmFlow';
import FilmSceneModal from './FilmSceneModal';
import PubPanel from './PubPanel';

function formatAr(n) { return `${(n || 0).toLocaleString('fr-FR')} Ar`; }

// TeckiA O (omni) a une duree FIXE de 10s imposee par Google - au-dela d'environ 55 mots dans
// le prompt (dialogue + descriptions comprises), le modele n'arrive plus a caser la parole
// synchronisee dans ces 10s et rejette la generation (422 "Could not generate a video with the
// given inputs" - constate en reel le 2026-10-03 avec un prompt de 195 mots / 4 personnages).
// Heuristique volontairement simple (comptage de mots, pas de vraie analyse du dialogue) :
// mieux vaut un faux positif occasionnel sur une longue description qu'un echec silencieux.
const OMNI_WORD_WARNING_THRESHOLD = 55;

function OmniDurationWarning({ provider, text }) {
  if (provider !== 'omni') return null;
  const wordCount = (text || '').trim().split(/\s+/).filter(Boolean).length;
  if (wordCount <= OMNI_WORD_WARNING_THRESHOLD) return null;
  return (
    <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2 mb-3 -mt-1">
      <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
      <p className="text-amber-200 text-[11px] leading-snug">
        Ce texte ({wordCount} mots) risque d'être trop long pour les 10 secondes fixes de TeckiA O —
        le dialogue ne tiendra pas et la génération sera probablement refusée. Gardez 1-2 répliques
        courtes par scène, et découpez le reste en plusieurs scènes (avec "continuité" activée).
      </p>
    </div>
  );
}

const MVOLA_NUMBER = '034 26 126 52';
const MVOLA_ACCOUNT_NAME = 'TDI DIGITAL';
const ORANGE_NUMBER = '032 89 653 75';
const ORANGE_ACCOUNT_NAME = 'HERY NY AINA MIADANTSOA Nomenjanahary';

// Mode "Champs detailles" (alternative au prompt libre) : categories de base reprises de la
// bibliotheque de templates CM Studio et de TeckiA Image Studio (Environnement/Ambiance/Tenue/
// Mouvement/Action), etendues avec Personnage (visage/acteur) et Info complementaire.
const STRUCTURED_FIELDS = [
  { key: 'environment', label: 'Environnement', placeholder: 'Ex : un salon moderne baigné de lumière naturelle...' },
  { key: 'ambiance', label: 'Ambiance', placeholder: 'Ex : chaleureuse, premium, lumière dorée du soir...' },
  { key: 'character', label: 'Personnage', placeholder: 'Ex : le visage de @image1, jeune femme malgache souriante...' },
  { key: 'outfit', label: 'Tenue', placeholder: 'Ex : la tenue de @image2...' },
  { key: 'movement', label: 'Mouvement', placeholder: 'Ex : légère rotation de la caméra...' },
  { key: 'action', label: 'Action', placeholder: 'Ex : tient le produit @image1 et sourit...' },
  { key: 'info', label: 'Info complémentaire', placeholder: 'Ex : tout autre détail important pour cette génération...' },
];

// veo_lite/veo_fast/veo_quality partagent une config strictement identique cote backend
// (seuls label + prix changent, cf VIDEO_PROVIDERS dans ia_studio_routes.py) : on les regroupe
// en une seule tuile "TeckiA V" dans le picker, le choix Lite/Fast/Quality se faisant ensuite
// via un selecteur de qualite dans la zone options (a cote resolution/duree).
const VEO_TIERS = [
  { key: 'veo_lite', short: 'Lite' },
  { key: 'veo_fast', short: 'Fast' },
  { key: 'veo_quality', short: 'Quality' },
];
const isVeoTierKey = (key) => VEO_TIERS.some(t => t.key === key);
const VEO_GROUP_INFO = "Gamme TeckiA V (Google Veo) : jusqu'à la 4K, 8 secondes, image vers vidéo. "
  + "Choisissez ensuite la qualité (Lite/Fast/Quality) juste en dessous.";

// Identite visuelle par provider (tuile "boite 3D" compacte du picker Style) : chaque provider
// a sa propre couleur + icone pour etre reconnaissable d'un coup d'oeil, meme principe que les
// ServiceCard de ClientDashboard.jsx (degrade unique par module) mais adapte a une grille dense.
const PROVIDER_THEME = {
  grok:           { icon: Wand2,         color: 'from-sky-500 to-blue-600',      glow: 'shadow-[0_0_18px_rgba(56,189,248,0.5)]' },
  omni:           { icon: MessageSquare, color: 'from-fuchsia-500 to-pink-600',  glow: 'shadow-[0_0_18px_rgba(217,70,239,0.5)]' },
  seedance_mini:  { icon: Zap,           color: 'from-emerald-500 to-teal-600',  glow: 'shadow-[0_0_18px_rgba(16,185,129,0.5)]' },
  veo_group:      { icon: Sparkles,      color: 'from-amber-500 to-orange-600',  glow: 'shadow-[0_0_18px_rgba(245,158,11,0.5)]' },
  infinitalk:     { icon: Music2,        color: 'from-rose-500 to-red-600',      glow: 'shadow-[0_0_18px_rgba(244,63,94,0.5)]' },
  motion_control: { icon: Clapperboard,  color: 'from-indigo-500 to-violet-600', glow: 'shadow-[0_0_18px_rgba(99,102,241,0.5)]' },
  wan_prime:      { icon: Zap,           color: 'from-cyan-500 to-blue-600',     glow: 'shadow-[0_0_18px_rgba(6,182,212,0.5)]' },
  seedance_25:    { icon: Sparkles,      color: 'from-violet-500 to-purple-700', glow: 'shadow-[0_0_18px_rgba(139,92,246,0.5)]' },
  wan3:           { icon: Zap,           color: 'from-lime-500 to-emerald-600',  glow: 'shadow-[0_0_18px_rgba(132,204,22,0.5)]' },
};
const providerTheme = (key) => PROVIDER_THEME[key] || PROVIDER_THEME.grok;

function TeckiALoader({ subtitle }) {
  return (
    <div className="flex flex-col items-center justify-center gap-5 py-12">
      <div className="relative w-36 h-36 flex items-center justify-center">
        <div className="ia-loader-radar absolute w-24 h-24 rounded-full border-2 border-purple-400" />
        <div className="ia-loader-ring absolute w-32 h-32">
          {[0, 90, 180, 270].map(deg => (
            <div
              key={deg}
              className="absolute w-2.5 h-2.5 rounded-full bg-purple-300"
              style={{
                boxShadow: '0 0 8px 2px rgba(139,92,246,0.8)',
                top: deg === 0 ? 0 : deg === 180 ? 'auto' : '50%',
                bottom: deg === 180 ? 0 : 'auto',
                left: deg === 270 ? 0 : deg === 90 ? 'auto' : '50%',
                right: deg === 90 ? 0 : 'auto',
                transform: (deg === 0 || deg === 180) ? 'translateX(-50%)' : (deg === 90 || deg === 270) ? 'translateY(-50%)' : undefined,
              }}
            />
          ))}
        </div>
        <div className="ia-loader-orb w-[76px] h-[76px] rounded-3xl overflow-hidden shadow-[0_0_24px_rgba(139,92,246,0.6)]">
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-300 via-purple-500 to-indigo-600">
            <Film className="w-7 h-7 text-white" />
          </div>
        </div>
      </div>
      <div className="text-center">
        <div className="text-white font-bold text-sm">TeckiA génère votre vidéo...</div>
        <div className="text-gray-400 text-xs mt-1">{subtitle}</div>
      </div>
    </div>
  );
}

function Retention14DaysNotice() {
  return (
    <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-2xl p-4 flex items-start gap-3">
      <Sparkles className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-amber-300 font-bold text-sm mb-0.5">💡 Astuce</p>
        <p className="text-gray-300 text-xs">Les vidéos générées sont disponibles 14 jours. Téléchargez-les rapidement pour ne pas les perdre !</p>
      </div>
    </div>
  );
}

function TopupModal({ onClose, onSubmit, submitting, serviceId, onCardSuccess }) {
  // Etat local (pas remonte au parent) : le parent IAStudioVideoPanel a des dizaines de
  // useState (storyboard, generation...) et re-rendait tout l'ecran a chaque frappe dans ce
  // champ quand amount/method/ref_ vivaient la-bas - latence visible signalee par Hery le
  // 2026-08-20. onSubmit recoit desormais les valeurs directement, comme deja fait cote mobile.
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('mvola');
  const [ref_, setRef] = useState('');
  // Depuis la refonte tarifaire du 2026-08-24 : la carte n'accepte plus un montant libre, le
  // client choisit un pack (bonus credite en plus du prix paye). MVola et Orange restent en
  // montant libre, credites 1:1 sans bonus (declaration manuelle + validation admin).
  //
  // MVola repasse en manuel le 2026-08-28 : meme avec les headers exacts recommandes par le
  // support MVola (X-CorrelationID timestamp, UserLanguage MG, sans UserAccountIdentifier ni
  // partnerName en header), la meme erreur "Missing field"/formatError/mmErrorCode 4001 persiste
  // - probablement une config du compte marchand plutot qu'un souci de requete. Mail de suivi
  // envoye avec date/heure exacte + X-CorrelationID + reponse complete, comme demande par MVola.
  const [packs, setPacks] = useState([]);
  const [loadingPacks, setLoadingPacks] = useState(false);
  const [selectedPackId, setSelectedPackId] = useState(null);

  useEffect(() => {
    if (method !== 'card' || packs.length || loadingPacks) return;
    setLoadingPacks(true);
    axios.get(`${API_URL}/api/ia-studio/credit-packs`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
    }).then(res => {
      if (res.data.success) setPacks(res.data.packs);
    }).finally(() => setLoadingPacks(false));
  }, [method]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedPack = packs.find(p => p.pack_id === selectedPackId) || null;

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className={`bg-[#140a24] border border-purple-500/25 rounded-2xl w-full p-5 overflow-hidden transition-all ${method === 'card' && selectedPack ? 'max-w-md' : 'max-w-sm'}`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold text-base">Recharger mes crédits vidéo</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
        <label className="block text-white text-xs font-semibold mb-1.5">Méthode de paiement</label>
        <div className="flex gap-2 mb-3">
          {[{ id: 'mvola', label: 'MVola' }, { id: 'orange', label: 'Orange Money' }, { id: 'card', label: 'Carte bancaire' }].map(m => (
            <button
              key={m.id} onClick={() => setMethod(m.id)}
              className={`flex-1 py-2 rounded-xl text-xs font-bold border transition ${method === m.id ? 'bg-purple-600 border-purple-400 text-white' : 'bg-white/5 border-white/10 text-gray-400'}`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {method === 'card' ? (
          <>
            <label className="block text-white text-xs font-semibold mb-1.5">Choisissez un pack</label>
            {loadingPacks ? (
              <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 text-purple-400 animate-spin" /></div>
            ) : (
              <div className="grid grid-cols-2 gap-2 mb-3">
                {packs.map(p => (
                  <button
                    key={p.pack_id}
                    onClick={() => setSelectedPackId(p.pack_id)}
                    className={`text-left p-2.5 rounded-xl border transition ${selectedPackId === p.pack_id ? 'bg-purple-600/30 border-purple-400' : 'bg-white/5 border-white/10 hover:border-white/20'}`}
                  >
                    <div className="text-white text-sm font-bold">{formatAr(p.price_ar)}</div>
                    <div className="text-purple-300 text-[11px] font-semibold">→ {formatAr(p.credited_ar)}</div>
                    {p.bonus_percent > 0 && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-amber-300">
                        <Sparkles className="w-3 h-3" /> +{p.bonus_percent}%
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
            {selectedPack ? (
              <StripePaymentForm
                key={selectedPack.pack_id}
                cart={[{ service: serviceId, pack_id: selectedPack.pack_id, quantity: 1 }]}
                getTotalAmount={() => selectedPack.price_ar}
                userId={localStorage.getItem('user_id')}
                onSuccess={onCardSuccess}
                onCancel={onClose}
              />
            ) : (
              !loadingPacks && (
                <p className="text-gray-500 text-[11px] mb-1">Sélectionnez un pack pour continuer.</p>
              )
            )}
          </>
        ) : (
          <>
            <label className="block text-white text-xs font-semibold mb-1.5">Montant (Ariary)</label>
            <input
              type="number" value={amount} onChange={e => setAmount(e.target.value)}
              placeholder="Ex : 20000"
              className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-400 mb-3"
            />
            <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-3 text-[11px] text-purple-100 mb-3">
              Envoyez votre paiement au numéro <span className="font-semibold">{method === 'mvola' ? MVOLA_NUMBER : ORANGE_NUMBER}</span>{' '}
              au nom de <span className="font-semibold">{method === 'mvola' ? MVOLA_ACCOUNT_NAME : ORANGE_ACCOUNT_NAME}</span>, puis indiquez la référence reçue par SMS ci-dessous.
            </div>
            <label className="block text-white text-xs font-semibold mb-1.5">Référence de paiement</label>
            <input
              value={ref_} onChange={e => setRef(e.target.value)}
              placeholder="Numéro de transaction..."
              className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-400 mb-4"
            />
            <p className="text-gray-500 text-[11px] mb-4">
              Effectuez le paiement puis déclarez-le ici. Votre solde sera crédité après vérification.
            </p>
            <button
              onClick={() => onSubmit(amount, method, ref_)}
              disabled={!amount || !ref_.trim() || submitting}
              className="w-full py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2 disabled:opacity-40 transition"
              style={{ background: 'linear-gradient(135deg, #9333EA, #DB2777)' }}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />}
              Déclarer le paiement
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const FILM_IMPORT_TEMPLATE = `Écris-moi un film de [durée] pour une IA de génération vidéo, en suivant exactement ce plan :
TITRE
STYLE VISUEL (éclairage, ambiance, look de cinéma)
PERSONNAGES : pour chacun, le nom, l'âge, l'apparence physique et la tenue
SCÈNES : numérote chaque scène (SCÈNE 1, SCÈNE 2…) avec sa durée en secondes (10 s en général). Pour chaque scène, un paragraphe qui décrit le plan : cadrage, lieu, lumière, actions dans l'ordre, puis les dialogues entre guillemets avec le nom de celui qui parle, puis l'ambiance sonore.
Quand une scène continue exactement la précédente (même lieu, même instant), écris « suite de la scène précédente » ; sinon c'est une nouvelle scène.`;

export default function IAStudioVideoPanel({ onBack, seed, onSeedConsumed }) {
  const token = localStorage.getItem('auth_token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  const [mode, setMode] = useState('normal'); // 'normal' | 'storyboard' | 'film' | 'pub'

  // Réalisateur IA ("Création / narration") : brief -> script (validation client) -> production
  // automatisée (personnages puis scènes enchaînées, réutilise le storyboard existant en coulisses).
  const [filmBrief, setFilmBrief] = useState('');
  const [filmDuration, setFilmDuration] = useState(60);
  const [filmAspectRatio, setFilmAspectRatio] = useState('16:9');
  const [filmLanguage, setFilmLanguage] = useState('fr'); // 'fr' | 'en' | 'mg' - choisi par selecteur, jamais deduit d'un texte libre
  const [filmStage, setFilmStage] = useState('brief'); // 'brief' | 'questions' | 'script' | 'production'
  const [filmQuestions, setFilmQuestions] = useState([]);
  const [filmAnswers, setFilmAnswers] = useState([]);
  const [filmLoadingQuestions, setFilmLoadingQuestions] = useState(false);
  const [filmScript, setFilmScript] = useState(null);
  const [filmCost, setFilmCost] = useState(null);
  const [filmPrompts, setFilmPrompts] = useState([]); // prompts REELS envoyes au moteur (apercu serveur)
  const [filmGeneratingScript, setFilmGeneratingScript] = useState(false);
  const [filmShowExtra, setFilmShowExtra] = useState(false);   // bouton « Ajouter des infos » (uniquement APRÈS avoir lu l'histoire)
  const [filmExtraInfo, setFilmExtraInfo] = useState('');
  const [filmScriptError, setFilmScriptError] = useState(null);
  const [filmStoryboardId, setFilmStoryboardId] = useState(null);
  const [filmProducing, setFilmProducing] = useState(false);
  const [filmProduceError, setFilmProduceError] = useState(null);
  const [filmStatus, setFilmStatus] = useState(null);
  const [filmSource, setFilmSource] = useState('ai');           // 'ai' : TeckiA invente le film | 'import' : le client colle un film deja prepare
  const [filmImportText, setFilmImportText] = useState('');
  const [filmImporting, setFilmImporting] = useState(false);
  const [filmSel, setFilmSel] = useState(null);                 // scene ouverte dans le panneau de verification
  const filmPollRef = useRef(null);
  const filmAdvancingRef = useRef(false);
  const [providers, setProviders] = useState({});
  const [credits, setCredits] = useState(0);
  const [showTopup, setShowTopup] = useState(false);
  const [showMyStats, setShowMyStats] = useState(false);
  const [trimModal, setTrimModal] = useState(null); // { file, maxDuration, onConfirm, onCancel }
  const [audioTrimModal, setAudioTrimModal] = useState(null); // { file, maxDuration, onConfirm, onCancel }
  const [submittingTopup, setSubmittingTopup] = useState(false);

  // ── Mode normal ──
  const [provider, setProvider] = useState('');
  const [promptMode, setPromptMode] = useState('free'); // 'free' | 'structured'
  const [brief, setBrief] = useState('');
  const [generatingPrompt, setGeneratingPrompt] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [fieldEnvironment, setFieldEnvironment] = useState('');
  const [fieldAmbiance, setFieldAmbiance] = useState('');
  const [fieldCharacter, setFieldCharacter] = useState('');
  const [fieldOutfit, setFieldOutfit] = useState('');
  const [fieldMovement, setFieldMovement] = useState('');
  const [fieldAction, setFieldAction] = useState('');
  const [fieldInfo, setFieldInfo] = useState('');
  const [structuredFocus, setStructuredFocus] = useState('action');
  const [resolution, setResolution] = useState('');
  const [aspectRatio, setAspectRatio] = useState('');
  const [duration, setDuration] = useState(0);
  const [refFiles, setRefFiles] = useState([]);
  const [refPreviews, setRefPreviews] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [refVideoFiles, setRefVideoFiles] = useState([]);
  const [refAudioFiles, setRefAudioFiles] = useState([]);
  const [firstFrameFile, setFirstFrameFile] = useState(null);
  const [firstFramePreview, setFirstFramePreview] = useState(null);
  const [lastFrameFile, setLastFrameFile] = useState(null);
  const [lastFramePreview, setLastFramePreview] = useState(null);
  const [generateAudio, setGenerateAudio] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const fileInputRef = useRef(null);
  const fieldFileInputRef = useRef(null);
  const [pendingFieldKey, setPendingFieldKey] = useState(null);
  const videoInputRef = useRef(null);
  const refVideoInputRef = useRef(null);
  const refAudioInputRef = useRef(null);
  const firstFrameInputRef = useRef(null);
  const lastFrameInputRef = useRef(null);
  const pollRef = useRef(null);
  const pollTimeoutRef = useRef(null);

  // ── Mode storyboard ──
  const [storyboards, setStoryboards] = useState([]);
  const [loadingStoryboards, setLoadingStoryboards] = useState(false);
  const [activeStoryboard, setActiveStoryboard] = useState(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);
  const [sceneProvider, setSceneProvider] = useState('');
  const [scenePromptMode, setScenePromptMode] = useState('free'); // 'free' | 'structured'
  const [sceneBrief, setSceneBrief] = useState('');
  const [generatingScenePrompt, setGeneratingScenePrompt] = useState(false);
  const [scenePrompt, setScenePrompt] = useState('');
  const [sceneFieldEnvironment, setSceneFieldEnvironment] = useState('');
  const [sceneFieldAmbiance, setSceneFieldAmbiance] = useState('');
  const [sceneFieldCharacter, setSceneFieldCharacter] = useState('');
  const [sceneFieldOutfit, setSceneFieldOutfit] = useState('');
  const [sceneFieldMovement, setSceneFieldMovement] = useState('');
  const [sceneFieldAction, setSceneFieldAction] = useState('');
  const [sceneFieldInfo, setSceneFieldInfo] = useState('');
  const [sceneStructuredFocus, setSceneStructuredFocus] = useState('action');
  const [sceneResolution, setSceneResolution] = useState('');
  const [sceneAspectRatio, setSceneAspectRatio] = useState('');
  const [sceneDuration, setSceneDuration] = useState(0);
  const [sceneRefFiles, setSceneRefFiles] = useState([]);
  const [sceneRefPreviews, setSceneRefPreviews] = useState([]);
  const [sceneVideoFile, setSceneVideoFile] = useState(null);
  const [sceneVideoPreview, setSceneVideoPreview] = useState(null);
  const [sceneRefVideoFiles, setSceneRefVideoFiles] = useState([]);
  const [sceneRefAudioFiles, setSceneRefAudioFiles] = useState([]);
  const [sceneFirstFrameFile, setSceneFirstFrameFile] = useState(null);
  const [sceneFirstFramePreview, setSceneFirstFramePreview] = useState(null);
  const [sceneLastFrameFile, setSceneLastFrameFile] = useState(null);
  const [sceneLastFramePreview, setSceneLastFramePreview] = useState(null);
  const [sceneGenerateAudio, setSceneGenerateAudio] = useState(false);
  // Continuite explicite (case a cocher) plutot qu'un comportement implicite : le backend
  // combine desormais la derniere frame avec d'eventuelles autres photos de reference au lieu
  // de laisser l'une ecraser l'autre (demande explicite de Hery, 2026-08-19).
  const [sceneUseLastFrame, setSceneUseLastFrame] = useState(true);
  const [addingScene, setAddingScene] = useState(false);
  // Non-null = le composeur "Nouvelle scene" est en fait en train de MODIFIER/regenerer cette
  // scene existante (completed/failed) plutot que d'en ajouter une nouvelle - meme formulaire
  // reutilise, juste une route et un libelle differents (demande explicite de Hery, 2026-08-19).
  const [editingSceneId, setEditingSceneId] = useState(null);
  const [assembling, setAssembling] = useState(false);
  const scenePollRef = useRef(null);
  const sceneFileInputRef = useRef(null);
  const sceneFieldFileInputRef = useRef(null);
  const [pendingSceneFieldKey, setPendingSceneFieldKey] = useState(null);
  const sceneVideoInputRef = useRef(null);
  const sceneRefVideoInputRef = useRef(null);
  const sceneRefAudioInputRef = useRef(null);
  const sceneFirstFrameInputRef = useRef(null);
  const sceneLastFrameInputRef = useRef(null);

  useEffect(() => {
    loadProviders();
    loadCredits();
    loadHistory();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
      if (scenePollRef.current) clearInterval(scenePollRef.current);
      if (filmPollRef.current) clearInterval(filmPollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if ((mode === 'storyboard' || mode === 'film') && storyboards.length === 0) loadStoryboards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Arrivee depuis "Utiliser en vidéo" (Image Studio, voir ClientDashboard) : on recupere
  // l'image deja generee (URL publique, pas besoin de re-upload manuel) et on la depose comme
  // photo de reference dans le mode choisi (normal/storyboard), une seule fois (onSeedConsumed
  // vide le seed cote parent pour ne pas re-ajouter la reference a chaque re-render).
  useEffect(() => {
    if (!seed?.imageUrl) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(seed.imageUrl);
        const blob = await resp.blob();
        const file = new File([blob], 'reference.png', { type: blob.type || 'image/png' });
        if (cancelled) return;
        const reader = new FileReader();
        reader.onload = e => {
          if (seed.mode === 'storyboard') setSceneRefPreviews(prev => [...prev, e.target.result]);
          else setRefPreviews(prev => [...prev, e.target.result]);
        };
        reader.readAsDataURL(file);
        if (seed.mode === 'storyboard') {
          setMode('storyboard');
          setSceneRefFiles(prev => [...prev, file]);
        } else {
          setMode('normal');
          setRefFiles(prev => [...prev, file]);
        }
      } catch (e) { /* silencieux */ }
      onSeedConsumed?.();
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed]);

  const loadProviders = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ia-studio/video/providers`, { headers: authHeaders });
      if (res.data.success) {
        setProviders(res.data.providers);
        const first = Object.keys(res.data.providers)[0];
        if (first) {
          const cfg = res.data.providers[first];
          setProvider(first); setResolution(cfg.resolutions[0]); setDuration(cfg.default_duration || cfg.max_duration);
          setAspectRatio(cfg.aspect_ratios[0]);
          setSceneProvider(first); setSceneResolution(cfg.resolutions[0]); setSceneDuration(cfg.default_duration || cfg.max_duration);
          setSceneAspectRatio(cfg.aspect_ratios[0]);
        }
      }
    } catch (e) { /* silencieux */ }
  };

  // Etape 0 : avant d'ecrire le script, le realisateur IA pose quelques questions de
  // clarification (comme un vrai producteur) - le client repond, ou passe directement si l'IA
  // n'a rien a demander (brief deja tres precis).
  const askFilmQuestions = async () => {
    if (!filmBrief.trim() || filmLoadingQuestions) return;
    setFilmLoadingQuestions(true);
    setFilmScriptError(null);
    try {
      const res = await axios.post(`${API_URL}/api/ia-studio/video/film/questions`, {
        brief: filmBrief.trim(), language: filmLanguage,
      }, { headers: authHeaders });
      const questions = (res.data.success && res.data.questions) || [];
      if (questions.length === 0) {
        // Rien a demander - on ecrit directement le script.
        await generateFilmScript([]);
      } else {
        setFilmQuestions(questions);
        setFilmAnswers(questions.map(q => ({ question: q, answer: '' })));
        setFilmStage('questions');
      }
    } catch (e) {
      // Les questions sont un bonus, jamais bloquant - on part directement sur le script.
      await generateFilmScript([]);
    }
    setFilmLoadingQuestions(false);
  };

  // L'IA écrit directement l'histoire à partir de la demande (tout y est : personnages, ambiance, style...).
  // Les précisions viennent APRÈS, via « Ajouter des infos » sur l'écran de l'histoire (extra = ce que le client ajoute).
  const generateFilmScript = async (answersOverride, extra) => {
    if (!filmBrief.trim() || filmGeneratingScript) return;
    setFilmGeneratingScript(true);
    setFilmScriptError(null);
    try {
      const body = {
        brief: filmBrief.trim(), duration: filmDuration, aspect_ratio: filmAspectRatio, language: filmLanguage,
        answers: answersOverride !== undefined ? answersOverride : filmAnswers,
      };
      if (extra && filmScript) { body.extra_info = extra; body.previous = { title: filmScript.title, summary: filmScript.summary, story: filmScript.story }; }
      const res = await axios.post(`${API_URL}/api/ia-studio/video/film/script`, body, { headers: authHeaders });
      if (res.data.success) {
        setFilmScript(res.data.script);
        setFilmCost(res.data.cost);
        setFilmPrompts(res.data.prompts || []);
        setFilmSummaryMg(null); setFilmSummaryLang('fr'); setFilmUndo(null); setFilmNotice('');
        setFilmShowExtra(false); setFilmExtraInfo('');
        setFilmStage('story');
      } else {
        setFilmScriptError(res.data.error || "Erreur lors de l'écriture de l'histoire");
        if (!filmScript) setFilmStage('brief');
      }
    } catch (e) {
      setFilmScriptError(e?.response?.data?.error || "Erreur lors de l'écriture de l'histoire");
      if (!filmScript) setFilmStage('brief');
    }
    setFilmGeneratingScript(false);
  };

  // Reprendre un film depuis l'historique : la production peut encore etre pilotable (plan
  // toujours en Redis, TTL 24h) ou seulement consultable si la session de production a expire
  // (le storyboard Appwrite, lui, ne disparaît jamais - on retombe alors sur la vue Storyboard
  // classique, qui affiche les memes scenes/film assemble sans le pilotage automatique).
  const resumeFilm = async (storyboardId) => {
    try {
      const res = await axios.get(`${API_URL}/api/ia-studio/video/film/${storyboardId}/status?full=1`, { headers: authHeaders });
      if (res.data.success) {
        setFilmStoryboardId(storyboardId);
        setFilmStatus(res.data);
        setFilmSel(null);
        setFilmScript(res.data.graph
          ? { title: res.data.title, summary: res.data.summary, visual_style: res.data.visual_style, language: res.data.language, aspect_ratio: res.data.aspect_ratio, music: res.data.music, teckia_mark: res.data.teckia_mark, scenes: res.data.plan_scenes || [], characters: res.data.characters || [] }
          : { title: res.data.title, summary: res.data.summary, scenes: [], characters: [] });
        setFilmStage('production');
        if (res.data.status !== 'assembled') pollFilmStatus(storyboardId);
        return;
      }
    } catch (e) { /* voir repli ci-dessous */ }
    await openStoryboard(storyboardId);
    setMode('storyboard');
  };

  const pollFilmStatus = (storyboardId) => {
    if (filmPollRef.current) clearInterval(filmPollRef.current);
    let n = 0;
    let lastDone = -1;
    let full = true;
    const stop = () => { clearInterval(filmPollRef.current); filmPollRef.current = null; };
    const tick = async () => {
      n += 1;
      try {
        const res = await axios.get(`${API_URL}/api/ia-studio/video/film/${storyboardId}/status${full ? '?full=1' : ''}`, { headers: authHeaders });
        if (!res.data.success) return;
        if (res.data.graph) {
          if (res.data.plan_scenes) full = false;
          setFilmStatus(prev => ({ ...res.data, plan_scenes: res.data.plan_scenes || prev?.plan_scenes }));
        } else setFilmStatus(res.data);
        if (res.data.stage === 'error') { stop(); loadCredits(); return; }
        if (res.data.status === 'assembled') { stop(); loadCredits(); loadHistory(); loadStoryboards(); return; }

        // Mode flux : le serveur pilote tout seul (toutes les 20 s) ; le navigateur l'aide a reagir vite (toutes les ~9 s, ou des qu'une scene vient de se terminer).
        if (res.data.graph) {
          const done = (res.data.nodes || []).filter(x => x.state === 'completed').length;
          if (!filmAdvancingRef.current && (n % 3 === 1 || done !== lastDone)) {
            lastDone = done;
            filmAdvancingRef.current = true;
            try { await axios.post(`${API_URL}/api/ia-studio/video/film/${storyboardId}/step`, {}, { headers: authHeaders }); } catch (e) { /* le serveur reessaie tout seul */ }
            filmAdvancingRef.current = false;
          }
          return;
        }

        const scenes = res.data.scenes || [];
        const last = scenes[scenes.length - 1];
        // Ancien mode (films deja en cours) : des que la derniere scene lancee est terminee (ou echouee), on enchaine la suivante.
        if (last && (last.status === 'completed' || last.status === 'failed') && !filmAdvancingRef.current) {
          filmAdvancingRef.current = true;
          try {
            await axios.post(`${API_URL}/api/ia-studio/video/film/${storyboardId}/advance`, {}, { headers: authHeaders });
          } catch (e) { /* on retente au prochain tick (ex: 409 si le webhook n'a pas encore marque la scene terminee cote serveur) */ }
          filmAdvancingRef.current = false;
        }
      } catch (e) { /* on retente au prochain tick */ }
    };
    filmPollRef.current = setInterval(tick, 3000);
    tick();
  };

  const startFilmProduction = async () => {
    if (!filmScript || filmProducing) return;
    setFilmProducing(true);
    setFilmProduceError(null);
    try {
      const res = await axios.post(`${API_URL}/api/ia-studio/video/film/produce`, { script: filmScript, graph: true }, { headers: authHeaders });
      if (res.data.success) {
        setFilmStoryboardId(res.data.storyboard_id);
        setFilmSel(null);
        setFilmStage('production');
        pollFilmStatus(res.data.storyboard_id);
      } else {
        setFilmProduceError(res.data.message || res.data.error || 'Erreur au lancement de la production');
        if (res.data.error_code === 'insufficient_credit') setShowTopup(true);
      }
    } catch (e) {
      const d = e?.response?.data;
      setFilmProduceError(d?.message || d?.error || 'Erreur au lancement de la production');
      if (d?.error_code === 'insufficient_credit') setShowTopup(true);
    }
    setFilmProducing(false);
  };

  // Ecran de validation editable (demande Hery 2026-09-18) : le client relit et corrige titre,
  // resume, description des personnages et texte de chaque scene AVANT de lancer la production.
  const updateFilmField = (field, value) => setFilmScript(prev => ({ ...prev, [field]: value }));
  const updateCharacterField = (idx, field, value) => setFilmScript(prev => ({
    ...prev, characters: prev.characters.map((c, i) => i === idx ? { ...c, [field]: value } : c),
  }));
  // Toute modification d'un champ de la scene annule le prompt final modifie a la main (il serait obsolete).
  const updateSceneField = (idx, field, value) => setFilmScript(prev => ({
    ...prev, scenes: prev.scenes.map((sc, i) => i === idx ? { ...sc, [field]: value, ...(field === 'prompt_fr' || field === 'prompt_mg' || field === 'prompt_override' ? {} : { prompt_override: '' }) } : sc),
  }));
  const updateDialogueLine = (idx, di, key, value) => setFilmScript(prev => ({
    ...prev, scenes: prev.scenes.map((sc, i) => i === idx ? { ...sc, prompt_override: '', dialogue: (sc.dialogue || []).map((d, j) => j === di ? { ...d, [key]: value } : d) } : sc),
  }));
  // Rafraichit l'apercu du prompt reel (composition faite cote serveur : tags @imageN, continuite,
  // style visuel) - appele quand le client quitte un champ modifie, pas a chaque frappe.
  const refreshFilmPreview = async (scriptOverride) => {
    const sc = scriptOverride && scriptOverride.scenes ? scriptOverride : filmScript;
    if (!sc) return;
    try {
      const res = await axios.post(`${API_URL}/api/ia-studio/video/film/preview`, { script: sc }, { headers: authHeaders });
      if (res.data.success) setFilmPrompts(res.data.prompts || []);
    } catch (e) { /* silencieux, l'apercu precedent reste affiche */ }
  };

  // Image d'un personnage : generee par l'IA (payant), photo du client (gratuit) ou aucune.
  const setCharacterMode = (idx, mode) => {
    const next = { ...filmScript, characters: filmScript.characters.map((c, i) => i === idx ? { ...c, image_mode: mode } : c) };
    setFilmScript(next);
    refreshFilmPreview(next);
  };
  const uploadCharacterImage = async (idx, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('image', file);
    try {
      const res = await axios.post(`${API_URL}/api/ia-studio/video/film/upload-image`, fd, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        const next = { ...filmScript, characters: filmScript.characters.map((c, i) => i === idx ? { ...c, image_url: res.data.url, image_mode: 'upload' } : c) };
        setFilmScript(next);
        refreshFilmPreview(next);
      }
    } catch (e) {
      window.alert(e?.response?.data?.error || "Échec de l'envoi de l'image");
    }
  };

  // Traduction a la demande sur l'ecran de validation : to_fr = explication francaise du prompt
  // anglais ; to_en = le francais corrige par le client reecrit le prompt anglais.
  const [filmTranslating, setFilmTranslating] = useState({});
  const filmEditStartRef = useRef({});
  const translateScene = async (idx, direction) => {
    const sc = filmScript.scenes[idx];
    const text = direction === 'to_en' ? sc.prompt_fr : sc.prompt;
    if (!text || !text.trim()) return;
    setFilmTranslating(prev => ({ ...prev, [idx]: direction }));
    try {
      const res = await axios.post(`${API_URL}/api/ia-studio/video/film/translate`, {
        text, direction, language: filmScript.language || filmLanguage, context: sc.prompt,
      }, { headers: authHeaders });
      if (res.data.success) {
        const field = direction === 'to_en' ? 'prompt' : 'prompt_fr';
        const next = { ...filmScript, scenes: filmScript.scenes.map((x, i) => i === idx ? { ...x, [field]: res.data.text } : x) };
        setFilmScript(next);
        if (direction === 'to_en') refreshFilmPreview(next);
      }
    } catch (e) { /* silencieux, le texte actuel reste */ }
    setFilmTranslating(prev => { const n = { ...prev }; delete n[idx]; return n; });
  };

  // "Le mieux on lui fait choisir par scène" (demande explicite) : le client ajuste la résolution
  // de chaque scène après lecture du script, avant de lancer la production - le coût affiché
  // (voir filmLiveCost dans le rendu) se recalcule immédiatement avec la même formule que le
  // serveur (estimatePrice, déjà utilisé pour la génération normale/storyboard).
  const updateSceneResolution = (sceneIndex, resolution) => {
    setFilmScript(prev => ({
      ...prev,
      scenes: prev.scenes.map((s, i) => i === sceneIndex ? { ...s, resolution } : s),
    }));
  };

  // ---- Réalisateur : modifier une scène avec l'IA, insérer / supprimer, enchaînement, explications en malagasy
  const [filmAi, setFilmAi] = useState({});                        // { [index]: demande écrite par le client }
  const [filmAiBusy, setFilmAiBusy] = useState(null);              // 'edit-2' | 'insert-2' | 'mg' | 'sum'
  const [filmNotice, setFilmNotice] = useState('');
  const [filmUndo, setFilmUndo] = useState(null);                  // copie du plan avant la dernière modification IA / suppression
  const [filmExplainLang, setFilmExplainLang] = useState('fr');    // langue des explications affichées : 'fr' | 'mg'
  const [filmSummaryMg, setFilmSummaryMg] = useState(null);        // { summary, vision, logline } en malagasy
  const [filmSummaryLang, setFilmSummaryLang] = useState('fr');
  const renumberScenes = (list) => list.map((x, k) => ({ ...x, order: k + 1, uses_previous_frame: k === 0 ? false : !!x.uses_previous_frame }));
  const slimFilm = (sc) => ({ title: sc.title, visual_style: sc.visual_style, motif: sc.motif, language: sc.language || filmLanguage, characters: sc.characters, scenes: sc.scenes });

  // Traduit en malagasy les explications qui n'ont pas encore leur version malagasy (un seul appel pour toutes les scènes).
  const fillMg = async (scriptArg) => {
    const sc = scriptArg || filmScript;
    const need = (sc?.scenes || []).map(x => (x.prompt_fr && !x.prompt_mg ? x.prompt_fr : null)).filter(Boolean);
    if (!need.length) return;
    setFilmAiBusy('mg');
    try {
      const res = await axios.post(`${API_URL}/api/ia-studio/video/film/explain-mg`, { texts: need }, { headers: authHeaders });
      if (res.data.success) {
        setFilmScript(prev => ({ ...prev, scenes: prev.scenes.map(x => { const k = need.indexOf(x.prompt_fr); return k >= 0 && !x.prompt_mg ? { ...x, prompt_mg: res.data.texts[k] } : x; }) }));
      } else setFilmNotice(res.data.error || 'Traduction malagasy indisponible.');
    } catch (e) { setFilmNotice(e?.response?.data?.error || 'Traduction malagasy indisponible, réessayez.'); }
    setFilmAiBusy(null);
  };
  const chooseExplainLang = (lang) => { setFilmExplainLang(lang); if (lang === 'mg') fillMg(); };
  const redoSceneMg = (idx) => {
    const next = { ...filmScript, scenes: filmScript.scenes.map((x, i) => (i === idx ? { ...x, prompt_mg: '' } : x)) };
    setFilmScript(next);
    fillMg(next);
  };
  const chooseSummaryLang = async (lang) => {
    setFilmSummaryLang(lang);
    if (lang !== 'mg' || filmSummaryMg || !filmScript) return;
    setFilmAiBusy('sum');
    try {
      const res = await axios.post(`${API_URL}/api/ia-studio/video/film/explain-mg`, { texts: [filmScript.summary || '', filmScript.director_vision || '', filmScript.logline || ''] }, { headers: authHeaders });
      if (res.data.success) setFilmSummaryMg({ summary: res.data.texts[0], vision: res.data.texts[1], logline: res.data.texts[2] });
      else setFilmNotice(res.data.error || 'Traduction malagasy indisponible.');
    } catch (e) { setFilmNotice('Traduction malagasy indisponible, réessayez.'); }
    setFilmAiBusy(null);
  };

  // « Modifier avec l'IA » (mode edit) ou « Insérer une scène après » (mode insert) : l'IA relit tout le film pour garder la continuité.
  const aiEditScene = async (idx, mode) => {
    const text = (filmAi[idx] || '').trim();
    if (text.length < 3 || filmAiBusy) return;
    setFilmAiBusy(`${mode}-${idx}`); setFilmNotice('');
    try {
      const res = await axios.post(`${API_URL}/api/ia-studio/video/film/scene-edit`, { script: slimFilm(filmScript), index: idx, instruction: text, mode }, { headers: authHeaders });
      if (res.data.success) {
        setFilmUndo(filmScript);
        const list = [...filmScript.scenes];
        if (mode === 'insert') list.splice(idx + 1, 0, { ...res.data.scene, validated: false }); else list[idx] = { ...res.data.scene, validated: false };
        const next = { ...filmScript, scenes: renumberScenes(list) };
        setFilmScript(next);
        setFilmPrompts(res.data.prompts || []);
        if (res.data.cost) setFilmCost(res.data.cost);
        setFilmAi(p => ({ ...p, [idx]: '' }));
        setFilmNotice(res.data.warning || (mode === 'insert' ? `Scène ajoutée après la scène ${idx + 1}.` : `Scène ${idx + 1} modifiée.`));
        if (filmExplainLang === 'mg') fillMg(next);
      } else setFilmNotice(res.data.error || 'Modification impossible.');
    } catch (e) { setFilmNotice(e?.response?.data?.error || 'Modification impossible, reformulez votre demande.'); }
    setFilmAiBusy(null);
  };
  const deleteFilmScene = (idx) => {
    if (!filmScript || filmScript.scenes.length <= 2) return;
    if (!window.confirm(`Supprimer la scène ${idx + 1} du film ?`)) return;
    setFilmUndo(filmScript);
    const next = { ...filmScript, scenes: renumberScenes(filmScript.scenes.filter((_, k) => k !== idx)) };
    setFilmScript(next);
    setFilmSel(null);
    refreshFilmPreview(next);
    setFilmNotice(`Scène ${idx + 1} supprimée. Vérifiez l'enchaînement avec la scène suivante.`);
  };
  const toggleFilmChain = (idx) => {
    if (idx === 0) return;
    const next = { ...filmScript, scenes: filmScript.scenes.map((x, k) => (k === idx ? { ...x, uses_previous_frame: !x.uses_previous_frame, prompt_override: '' } : x)) };
    setFilmScript(next);
    refreshFilmPreview(next);
  };
  const undoFilmChange = () => {
    if (!filmUndo) return;
    setFilmScript(filmUndo);
    refreshFilmPreview(filmUndo);
    setFilmUndo(null);
    setFilmNotice('Modification annulée.');
  };

  // ---- Scène échouée (filtre de sécurité, erreur du moteur) : le client corrige le texte puis relance la scène
  const [filmFixText, setFilmFixText] = useState('');
  const [filmFixInstr, setFilmFixInstr] = useState('');
  const [filmFixBusy, setFilmFixBusy] = useState('');                 // 'ai' | 'text' | 'retry'
  const [filmFixNote, setFilmFixNote] = useState('');
  const activeFix = filmStatus?.graph ? (filmSel !== null ? filmStatus?.failed_scenes?.[String(filmSel)] : null) : filmStatus?.failed_scene;
  const failedKey = activeFix ? `${activeFix.index}|${activeFix.prompt_final}` : '';
  useEffect(() => { setFilmFixText(activeFix?.prompt_final || ''); }, [failedKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  const fixFilmScene = async (mode) => {
    if (!filmStatus?.failed_scene || filmFixBusy || !filmStoryboardId) return;
    setFilmFixBusy(mode); setFilmFixNote('');
    try {
      const body = { mode, ...(mode === 'ai' ? { instruction: filmFixInstr.trim() } : {}), ...(mode === 'text' ? { prompt: filmFixText } : {}) };
      const res = await axios.post(`${API_URL}/api/ia-studio/video/film/${filmStoryboardId}/fix-scene`, body, { headers: authHeaders });
      if (res.data.success) {
        setFilmFixNote(res.data.changes ? `✅ Scène relancée. ${res.data.changes}` : '✅ Scène relancée.');
        setFilmFixInstr('');
        loadCredits();
        pollFilmStatus(filmStoryboardId);
      } else setFilmFixNote(res.data.message || res.data.error || 'Correction impossible.');
    } catch (e) {
      const d = e?.response?.data;
      setFilmFixNote(d?.message || d?.error || 'Correction impossible, réessayez.');
    }
    setFilmFixBusy('');
  };

  const resetFilmFlow = () => {
    if (filmPollRef.current) { clearInterval(filmPollRef.current); filmPollRef.current = null; }
    setFilmStage('brief'); setFilmScript(null); setFilmCost(null); setFilmStoryboardId(null); setFilmShowExtra(false); setFilmExtraInfo('');
    setFilmStatus(null); setFilmScriptError(null); setFilmProduceError(null);
    setFilmBrief(''); setFilmQuestions([]); setFilmAnswers([]); setFilmPrompts([]);
    setFilmUndo(null); setFilmNotice(''); setFilmAi({}); setFilmSummaryMg(null); setFilmSummaryLang('fr'); setFilmFixNote(''); setFilmFixInstr('');
    loadStoryboards();
  };

  const loadCredits = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ia-studio/video/credits`, { headers: authHeaders });
      if (res.data.success) setCredits(res.data.balance);
    } catch (e) { /* silencieux */ }
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API_URL}/api/ia-studio/video/history`, { headers: authHeaders });
      if (res.data.success) setHistory(res.data.items);
    } catch (e) { /* silencieux */ }
    setLoadingHistory(false);
  };

  const deleteStoryboard = async (id, title) => {
    if (!window.confirm(`Supprimer définitivement « ${title || 'ce film'} » et ses vidéos ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/ia-studio/storyboard/${id}`, { headers: authHeaders });
      setStoryboards(prev => prev.filter(sb => sb.storyboard_id !== id));
      if (activeStoryboard?.storyboard_id === id) setActiveStoryboard(null);
    } catch (e) {
      window.alert(e?.response?.data?.error || 'Suppression impossible');
    }
  };

  const loadStoryboards = async () => {
    setLoadingStoryboards(true);
    try {
      const res = await axios.get(`${API_URL}/api/ia-studio/storyboard/list`, { headers: authHeaders });
      if (res.data.success) setStoryboards(res.data.items);
    } catch (e) { /* silencieux */ }
    setLoadingStoryboards(false);
  };

  const openStoryboard = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/api/ia-studio/storyboard/${id}`, { headers: authHeaders });
      if (res.data.success) setActiveStoryboard(res.data);
    } catch (e) { /* silencieux */ }
  };

  const createStoryboard = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/ia-studio/storyboard/create`, { title: 'Nouveau storyboard' }, { headers: authHeaders });
      if (res.data.success) {
        await loadStoryboards();
        openStoryboard(res.data.storyboard_id);
      }
    } catch (e) {
      window.alert(e?.response?.data?.message || e?.response?.data?.error || 'Erreur lors de la création');
    }
  };

  const startEditTitle = () => {
    setTitleDraft(activeStoryboard?.title || '');
    setEditingTitle(true);
  };

  const saveTitle = async () => {
    if (!titleDraft.trim() || !activeStoryboard || savingTitle) return;
    setSavingTitle(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/ia-studio/storyboard/${activeStoryboard.storyboard_id}/rename`,
        { title: titleDraft.trim() }, { headers: authHeaders },
      );
      if (res.data.success) {
        setActiveStoryboard(prev => ({ ...prev, title: res.data.title }));
        setStoryboards(prev => prev.map(sb => sb.storyboard_id === activeStoryboard.storyboard_id ? { ...sb, title: res.data.title } : sb));
        setEditingTitle(false);
      }
    } catch (e) { /* silencieux */ }
    setSavingTitle(false);
  };

  // Changer de moteur ne doit PAS reinitialiser ce que le client a deja configure (retour Hery
  // 2026-09-16 : il avait choisi 9:16 sur Omni, clique sur SM pour explorer, et le format
  // repartait silencieusement sur le defaut du nouveau provider - la generation partait donc
  // avec le mauvais format sans que rien ne le signale). On ne retombe sur le defaut du provider
  // QUE si le reglage actuel n'existe pas dans sa liste (resolution/aspect_ratio) ou le depasse
  // (duree) ; les references (video/audio/frames) ne sont videees que si le nouveau provider ne
  // les supporte pas du tout - sinon elles restent utilisables telles quelles.
  const onProviderChange = (p) => {
    setProvider(p);
    const cfg = providers[p];
    if (!cfg) return;
    setResolution(r => cfg.resolutions.includes(r) ? r : cfg.resolutions[0]);
    setAspectRatio(ar => cfg.aspect_ratios.includes(ar) ? ar : cfg.aspect_ratios[0]);
    setDuration(d => Math.min(d, cfg.max_duration));
    if (!cfg.supports_video_ref) { setVideoFile(null); setVideoPreview(null); setRefVideoFiles([]); }
    if (!cfg.supports_audio_ref) { setRefAudioFiles([]); }
    if (!cfg.supports_first_last_frame) {
      setFirstFrameFile(null); setFirstFramePreview(null);
      setLastFrameFile(null); setLastFramePreview(null);
    }
    if (!cfg.supports_generate_audio) setGenerateAudio(false);
  };

  // Meme correctif que onProviderChange (mode normal) applique au mode storyboard.
  const onSceneProviderChange = (p) => {
    setSceneProvider(p);
    const cfg = providers[p];
    if (!cfg) return;
    setSceneResolution(r => cfg.resolutions.includes(r) ? r : cfg.resolutions[0]);
    setSceneAspectRatio(ar => cfg.aspect_ratios.includes(ar) ? ar : cfg.aspect_ratios[0]);
    setSceneDuration(d => Math.min(d, cfg.max_duration));
    if (!cfg.supports_video_ref) { setSceneVideoFile(null); setSceneVideoPreview(null); setSceneRefVideoFiles([]); }
    if (!cfg.supports_audio_ref) { setSceneRefAudioFiles([]); }
    if (!cfg.supports_first_last_frame) {
      setSceneFirstFrameFile(null); setSceneFirstFramePreview(null);
      setSceneLastFrameFile(null); setSceneLastFramePreview(null);
    }
    if (!cfg.supports_generate_audio) setSceneGenerateAudio(false);
  };

  // Clic sur une tuile du picker (mode normal). Pour la tuile groupee "TeckiA V" : si un tier
  // veo est deja actif on ne touche a rien (seul le selecteur de qualite doit permettre de
  // changer entre Lite/Fast/Quality), sinon on bascule sur veo_lite (le moins cher) comme
  // premier choix par defaut.
  const onProviderTileClick = (tileKey) => {
    if (tileKey === 'veo_group') {
      if (isVeoTierKey(provider)) return;
      onProviderChange('veo_lite');
      return;
    }
    onProviderChange(tileKey);
  };

  const onSceneProviderTileClick = (tileKey) => {
    if (tileKey === 'veo_group') {
      if (isVeoTierKey(sceneProvider)) return;
      onSceneProviderChange('veo_lite');
      return;
    }
    onSceneProviderChange(tileKey);
  };

  // Changement de qualite Lite/Fast/Quality au sein de la gamme TeckiA V : config identique
  // entre les 3 tiers, donc contrairement a onProviderChange on NE reinitialise PAS
  // resolution/duree/aspect_ratio/references deja choisis par l'utilisateur.
  const onTierChange = (tierKey) => { setProvider(tierKey); };
  const onSceneTierChange = (tierKey) => { setSceneProvider(tierKey); };

  const resetSceneMedia = () => {
    setSceneRefFiles([]); setSceneRefPreviews([]);
    setSceneVideoFile(null); setSceneVideoPreview(null);
    setSceneRefVideoFiles([]); setSceneRefAudioFiles([]);
    setSceneFirstFrameFile(null); setSceneFirstFramePreview(null);
    setSceneLastFrameFile(null); setSceneLastFramePreview(null);
  };

  const addSceneRefFiles = (fileList, max) => {
    const room = max - sceneRefFiles.length;
    if (room <= 0) return;
    const arr = Array.from(fileList).slice(0, room);
    setSceneRefFiles(prev => [...prev, ...arr]);
    arr.forEach(f => {
      const reader = new FileReader();
      reader.onload = e => setSceneRefPreviews(prev => [...prev, e.target.result]);
      reader.readAsDataURL(f);
    });
  };
  const removeSceneRef = (idx) => {
    setSceneRefFiles(prev => prev.filter((_, i) => i !== idx));
    setSceneRefPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  // Toute vidéo de référence passe par VideoTrimModal (demande explicite utilisateur : le
  // bouton "Ajouter" ouvre le modal de découpage, pas un passthrough direct) — l'utilisateur
  // choisit toujours son segment, même si la vidéo tient déjà dans la limite du provider.
  // requestTrim() attend la résolution du modal (confirmation ou annulation) avant de rendre
  // la main à acceptVideoFile(s).
  // onAccept recoit (file, dureeSecondes) - meme principe que requestAudioTrim plus bas, pour
  // motion_control ou la duree video suit la duree de la video de reference fournie (pas de
  // slider libre, voir requires_video).
  const requestTrim = (file, maxDuration) => new Promise(resolve => {
    setTrimModal({
      file, maxDuration,
      onConfirm: (trimmedFile, dur) => { setTrimModal(null); resolve({ file: trimmedFile, duration: dur }); },
      onCancel: () => { setTrimModal(null); resolve(null); },
    });
  });

  const acceptVideoFile = async (file, maxDuration, onAccept) => {
    const trimmed = await requestTrim(file, maxDuration);
    if (trimmed) onAccept(trimmed.file, trimmed.duration);
  };

  const acceptVideoFiles = async (files, maxDuration, onAccept) => {
    for (const file of files) {
      // eslint-disable-next-line no-await-in-loop
      await acceptVideoFile(file, maxDuration, onAccept);
    }
  };

  // Meme principe que requestTrim/acceptVideoFile(s) ci-dessus, pour une reference AUDIO :
  // le modal s'ouvre systematiquement (previsualisation + decoupage) avant d'accepter le
  // fichier, jamais de passthrough direct.
  // onAccept recoit (file, dureeSecondes) - la duree vient du decoupage reel confirme dans
  // le modal, necessaire pour infinitalk ou la duree video suit la duree de l'audio (pas de
  // slider libre pour ce provider, voir requires_audio).
  const requestAudioTrim = (file, maxDuration) => new Promise(resolve => {
    setAudioTrimModal({
      file, maxDuration,
      onConfirm: (trimmedFile, dur) => { setAudioTrimModal(null); resolve({ file: trimmedFile, duration: dur }); },
      onCancel: () => { setAudioTrimModal(null); resolve(null); },
    });
  });

  const acceptAudioFile = async (file, maxDuration, onAccept) => {
    const trimmed = await requestAudioTrim(file, maxDuration);
    if (trimmed) onAccept(trimmed.file, trimmed.duration);
  };

  const acceptAudioFiles = async (files, maxDuration, onAccept) => {
    for (const file of files) {
      // eslint-disable-next-line no-await-in-loop
      await acceptAudioFile(file, maxDuration, onAccept);
    }
  };

  const onSceneVideoPicked = (file) => {
    if (!file) return;
    acceptVideoFile(file, (sceneCfg?.ref_video_max || sceneCfg?.max_duration), (f, dur) => {
      f._dur = dur || 0;
      setSceneVideoFile(f);
      setSceneVideoPreview(URL.createObjectURL(f));
      if (sceneCfg?.requires_video && dur) setSceneDuration(Math.round(dur));
    });
  };
  const removeSceneVideo = () => { setSceneVideoFile(null); setSceneVideoPreview(null); };

  const addSceneRefVideoFiles = (fileList, max) => {
    const room = max - sceneRefVideoFiles.length;
    if (room <= 0) return;
    const arr = Array.from(fileList).slice(0, room);
    acceptVideoFiles(arr, (sceneCfg?.ref_video_max || sceneCfg?.max_duration), (f, dur) => { f._dur = dur || 0; setSceneRefVideoFiles(prev => [...prev, f]); });
  };
  const removeSceneRefVideo = (idx) => setSceneRefVideoFiles(prev => prev.filter((_, i) => i !== idx));

  const addSceneRefAudioFiles = (fileList, max) => {
    const room = max - sceneRefAudioFiles.length;
    if (room <= 0) return;
    const arr = Array.from(fileList).slice(0, room);
    acceptAudioFiles(arr, (sceneCfg?.ref_audio_max || sceneCfg?.max_duration), (f, dur) => {
      setSceneRefAudioFiles(prev => [...prev, f]);
      if (sceneCfg?.requires_audio && dur) setSceneDuration(Math.round(dur));
    });
  };
  const removeSceneRefAudio = (idx) => setSceneRefAudioFiles(prev => prev.filter((_, i) => i !== idx));

  const onSceneFramePicked = (which, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      if (which === 'first') { setSceneFirstFrameFile(file); setSceneFirstFramePreview(e.target.result); }
      else { setSceneLastFrameFile(file); setSceneLastFramePreview(e.target.result); }
    };
    reader.readAsDataURL(file);
  };
  const removeSceneFrame = (which) => {
    if (which === 'first') { setSceneFirstFrameFile(null); setSceneFirstFramePreview(null); }
    else { setSceneLastFrameFile(null); setSceneLastFramePreview(null); }
  };

  const addRefFiles = (fileList) => {
    const room = (providerCfg?.max_images || 7) - refFiles.length;
    if (room <= 0) return;
    const arr = Array.from(fileList).slice(0, room);
    setRefFiles(prev => [...prev, ...arr]);
    arr.forEach(f => {
      const reader = new FileReader();
      reader.onload = e => setRefPreviews(prev => [...prev, e.target.result]);
      reader.readAsDataURL(f);
    });
  };

  const removeRef = (idx) => {
    setRefFiles(prev => prev.filter((_, i) => i !== idx));
    setRefPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const onVideoPicked = (file) => {
    if (!file) return;
    acceptVideoFile(file, (providerCfg?.ref_video_max || providerCfg?.max_duration), (f, dur) => {
      f._dur = dur || 0;
      setVideoFile(f);
      setVideoPreview(URL.createObjectURL(f));
      if (providerCfg?.requires_video && dur) setDuration(Math.round(dur));
    });
  };

  const removeVideo = () => { setVideoFile(null); setVideoPreview(null); };

  const addRefVideoFiles = (fileList, max) => {
    const room = max - refVideoFiles.length;
    if (room <= 0) return;
    const arr = Array.from(fileList).slice(0, room);
    acceptVideoFiles(arr, (providerCfg?.ref_video_max || providerCfg?.max_duration), (f, dur) => { f._dur = dur || 0; setRefVideoFiles(prev => [...prev, f]); });
  };
  const removeRefVideo = (idx) => setRefVideoFiles(prev => prev.filter((_, i) => i !== idx));

  const addRefAudioFiles = (fileList, max) => {
    const room = max - refAudioFiles.length;
    if (room <= 0) return;
    const arr = Array.from(fileList).slice(0, room);
    acceptAudioFiles(arr, (providerCfg?.ref_audio_max || providerCfg?.max_duration), (f, dur) => {
      setRefAudioFiles(prev => [...prev, f]);
      if (providerCfg?.requires_audio && dur) setDuration(Math.round(dur));
    });
  };
  const removeRefAudio = (idx) => setRefAudioFiles(prev => prev.filter((_, i) => i !== idx));

  const onFramePicked = (which, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      if (which === 'first') { setFirstFrameFile(file); setFirstFramePreview(e.target.result); }
      else { setLastFrameFile(file); setLastFramePreview(e.target.result); }
    };
    reader.readAsDataURL(file);
  };
  const removeFrame = (which) => {
    if (which === 'first') { setFirstFrameFile(null); setFirstFramePreview(null); }
    else { setLastFrameFile(null); setLastFramePreview(null); }
  };

  const structuredSetters = {
    environment: setFieldEnvironment, ambiance: setFieldAmbiance, character: setFieldCharacter, outfit: setFieldOutfit,
    movement: setFieldMovement, action: setFieldAction, info: setFieldInfo,
  };

  const insertImageTag = (n) => {
    const tag = `@image${n}`;
    const setter = promptMode === 'structured' ? structuredSetters[structuredFocus] : setPrompt;
    setter(prev => {
      const trimmed = (prev || '').trimEnd();
      if (trimmed.includes(tag)) return prev;
      return trimmed ? `${trimmed} ${tag} ` : `${tag} `;
    });
  };

  // Attache une image directement a un champ detaille (normal mode) : rejoint la banque de
  // references partagee (plafond providerCfg.max_images, <=7) et insere son tag @imageN dans
  // ce champ precis, sans passer par le focus manuel + clic sur vignette.
  const attachImageToField = (key, fileList) => {
    const max = providerCfg?.max_images || 7;
    const room = max - refFiles.length;
    if (room <= 0) {
      window.alert(`Maximum ${max} images de référence atteint.`);
      return;
    }
    const arr = Array.from(fileList).slice(0, room);
    if (arr.length === 0) return;
    const tag = `@image${refFiles.length + 1}`;
    setRefFiles(prev => [...prev, ...arr]);
    arr.forEach(f => {
      const reader = new FileReader();
      reader.onload = e => setRefPreviews(prev => [...prev, e.target.result]);
      reader.readAsDataURL(f);
    });
    structuredSetters[key](prev => {
      const trimmed = (prev || '').trimEnd();
      if (trimmed.includes(tag)) return prev;
      return trimmed ? `${trimmed} ${tag} ` : `${tag} `;
    });
  };

  const openFieldImagePicker = (key) => {
    const max = providerCfg?.max_images || 7;
    if (refFiles.length >= max) {
      window.alert(`Maximum ${max} images de référence atteint.`);
      return;
    }
    setPendingFieldKey(key);
    fieldFileInputRef.current?.click();
  };

  const generatePromptFromBrief = async () => {
    if (!brief.trim() || generatingPrompt) return;
    setGeneratingPrompt(true);
    try {
      const formData = new FormData();
      formData.append('brief', brief.trim());
      refFiles.forEach(f => formData.append('reference_images', f));
      const res = await axios.post(`${API_URL}/api/ia-studio/video/generate-prompt`, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) setPrompt(res.data.prompt);
    } catch (e) { /* silencieux */ }
    setGeneratingPrompt(false);
  };

  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    pollRef.current = null;
    pollTimeoutRef.current = null;
  };

  const cancelGeneration = () => {
    stopPolling();
    setGenerating(false);
    setCurrentJob({ status: 'failed', error: 'Génération annulée.' });
  };

  const pollVideoStatus = (jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/ia-studio/video/status/${jobId}`, { headers: authHeaders });
        const job = res.data;
        if (job.status === 'completed') {
          stopPolling();
          setCurrentJob({ status: 'completed', video_url: job.video_url });
          setGenerating(false);
          loadCredits(); loadHistory();
        } else if (job.status === 'failed') {
          stopPolling();
          setCurrentJob({ status: 'failed', error: job.error || 'Échec de la génération' });
          setGenerating(false);
        }
      } catch (e) { /* on retente au prochain tick */ }
    }, 4000);
  };

  const generate = async () => {
    if (!effectivePrompt.trim() || generating) return;
    stopPolling();
    setGenerating(true);
    setCurrentJob(null);
    try {
      const formData = new FormData();
      formData.append('provider', provider);
      if (promptMode === 'structured') {
        formData.append('field_environment', fieldEnvironment.trim());
        formData.append('field_ambiance', fieldAmbiance.trim());
        formData.append('field_character', fieldCharacter.trim());
        formData.append('field_outfit', fieldOutfit.trim());
        formData.append('field_movement', fieldMovement.trim());
        formData.append('field_action', fieldAction.trim());
        formData.append('field_info', fieldInfo.trim());
      } else {
        formData.append('prompt', prompt.trim());
      }
      formData.append('resolution', resolution);
      formData.append('aspect_ratio', aspectRatio);
      formData.append('duration', duration);
      formData.append('generate_audio', generateAudio ? 'true' : 'false');
      refFiles.forEach(f => formData.append('reference_images', f));
      if (videoFile) formData.append('reference_videos', videoFile);
      refVideoFiles.forEach(f => formData.append('reference_videos', f));
      refAudioFiles.forEach(f => formData.append('reference_audios', f));
      if (firstFrameFile) formData.append('first_frame', firstFrameFile);
      if (lastFrameFile) formData.append('last_frame', lastFrameFile);
      const res = await axios.post(`${API_URL}/api/ia-studio/video/generate`, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        setCurrentJob({ status: 'queued' });
        pollVideoStatus(res.data.job_id);
      } else {
        setGenerating(false);
      }
    } catch (e) {
      setGenerating(false);
      if (e?.response?.status === 402) {
        loadCredits();
        setCurrentJob({ status: 'failed', error: e.response.data?.message || 'Crédits insuffisants' });
      } else {
        setCurrentJob({ status: 'failed', error: e?.response?.data?.error || 'Erreur de génération' });
      }
    }
  };

  const pollActiveStoryboard = (storyboardId) => {
    if (scenePollRef.current) clearInterval(scenePollRef.current);
    scenePollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/ia-studio/storyboard/${storyboardId}`, { headers: authHeaders });
        if (res.data.success) {
          setActiveStoryboard(res.data);
          const scenes = res.data.scenes || [];
          if (!scenes.some(sc => sc.status === 'queued')) {          // on continue tant qu'UNE scene tourne (plusieurs peuvent tourner en parallele)
            clearInterval(scenePollRef.current);
            loadCredits();
          }
        }
      } catch (e) { /* on retente */ }
    }, 4000);
  };

  const sceneStructuredSetters = {
    environment: setSceneFieldEnvironment, ambiance: setSceneFieldAmbiance, character: setSceneFieldCharacter, outfit: setSceneFieldOutfit,
    movement: setSceneFieldMovement, action: setSceneFieldAction, info: setSceneFieldInfo,
  };

  const attachImageToSceneField = (key, fileList) => {
    const max = sceneCfg?.max_images || 7;
    const room = max - sceneRefFiles.length;
    if (room <= 0) {
      window.alert(`Maximum ${max} images de référence atteint.`);
      return;
    }
    const arr = Array.from(fileList).slice(0, room);
    if (arr.length === 0) return;
    const tag = `@image${sceneRefFiles.length + 1}`;
    setSceneRefFiles(prev => [...prev, ...arr]);
    arr.forEach(f => {
      const reader = new FileReader();
      reader.onload = e => setSceneRefPreviews(prev => [...prev, e.target.result]);
      reader.readAsDataURL(f);
    });
    sceneStructuredSetters[key](prev => {
      const trimmed = (prev || '').trimEnd();
      if (trimmed.includes(tag)) return prev;
      return trimmed ? `${trimmed} ${tag} ` : `${tag} `;
    });
  };

  const openSceneFieldImagePicker = (key) => {
    const max = sceneCfg?.max_images || 7;
    if (sceneRefFiles.length >= max) {
      window.alert(`Maximum ${max} images de référence atteint.`);
      return;
    }
    setPendingSceneFieldKey(key);
    sceneFieldFileInputRef.current?.click();
  };

  const insertSceneImageTag = (n) => {
    const tag = `@image${n}`;
    const setter = scenePromptMode === 'structured' ? sceneStructuredSetters[sceneStructuredFocus] : setScenePrompt;
    setter(prev => {
      const trimmed = (prev || '').trimEnd();
      if (trimmed.includes(tag)) return prev;
      return trimmed ? `${trimmed} ${tag} ` : `${tag} `;
    });
  };

  const generatePromptForScene = async () => {
    if (!sceneBrief.trim() || generatingScenePrompt) return;
    setGeneratingScenePrompt(true);
    try {
      const formData = new FormData();
      formData.append('brief', sceneBrief.trim());
      sceneRefFiles.forEach(f => formData.append('reference_images', f));
      const res = await axios.post(`${API_URL}/api/ia-studio/video/generate-prompt`, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) setScenePrompt(res.data.prompt);
    } catch (e) { /* silencieux */ }
    setGeneratingScenePrompt(false);
  };

  const addScene = async () => {
    if (!sceneEffectivePrompt.trim() || !activeStoryboard || addingScene) return;
    setAddingScene(true);
    try {
      const formData = new FormData();
      formData.append('provider', sceneProvider);
      if (scenePromptMode === 'structured') {
        formData.append('field_environment', sceneFieldEnvironment.trim());
        formData.append('field_ambiance', sceneFieldAmbiance.trim());
        formData.append('field_character', sceneFieldCharacter.trim());
        formData.append('field_outfit', sceneFieldOutfit.trim());
        formData.append('field_movement', sceneFieldMovement.trim());
        formData.append('field_action', sceneFieldAction.trim());
        formData.append('field_info', sceneFieldInfo.trim());
      } else {
        formData.append('prompt', scenePrompt.trim());
      }
      formData.append('resolution', sceneResolution);
      formData.append('aspect_ratio', sceneAspectRatio);
      formData.append('duration', sceneDuration);
      formData.append('generate_audio', sceneGenerateAudio ? 'true' : 'false');
      formData.append('use_last_frame', sceneUseLastFrame ? 'true' : 'false');
      sceneRefFiles.forEach(f => formData.append('reference_images', f));
      if (sceneVideoFile) formData.append('reference_videos', sceneVideoFile);
      sceneRefVideoFiles.forEach(f => formData.append('reference_videos', f));
      sceneRefAudioFiles.forEach(f => formData.append('reference_audios', f));
      if (sceneFirstFrameFile) formData.append('first_frame', sceneFirstFrameFile);
      if (sceneLastFrameFile) formData.append('last_frame', sceneLastFrameFile);
      const url = editingSceneId
        ? `${API_URL}/api/ia-studio/storyboard/${activeStoryboard.storyboard_id}/scene/${editingSceneId}/regenerate`
        : `${API_URL}/api/ia-studio/storyboard/${activeStoryboard.storyboard_id}/scene`;
      const res = await axios.post(url, formData, { headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) {
        setScenePrompt(''); setSceneBrief(''); resetSceneMedia();
        setSceneFieldEnvironment(''); setSceneFieldAmbiance(''); setSceneFieldOutfit('');
        setSceneFieldMovement(''); setSceneFieldAction('');
        setEditingSceneId(null);
        await openStoryboard(activeStoryboard.storyboard_id);
        pollActiveStoryboard(activeStoryboard.storyboard_id);
      }
    } catch (e) {
      if (e?.response?.status === 402) { loadCredits(); setShowTopup(true); }
      else window.alert(e?.response?.data?.error || e?.response?.data?.message || "Échec de l'ajout de la scène.");
    }
    setAddingScene(false);
  };

  // Pre-remplit le composeur "Nouvelle scene" avec les reglages d'une scene DEJA terminee/
  // echouee pour la regenerer en remplacement (pas en plus) - voir editingSceneId. Les
  // references (photos/videos/audios) ne sont pas retrouvables depuis la scene deja generee,
  // l'utilisateur devra les rejoindre lui-meme s'il en veut a nouveau.
  const startEditScene = (scene) => {
    setEditingSceneId(scene.scene_id);
    setSceneProvider(scene.provider);
    setSceneResolution(scene.resolution);
    setSceneDuration(scene.duration);
    setSceneAspectRatio(providers[scene.provider]?.aspect_ratios?.[0] || '');
    setScenePromptMode('free');
    setScenePrompt(scene.prompt || '');
    setSceneBrief('');
    resetSceneMedia();
    document.getElementById('scene-composer')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const cancelEditScene = () => {
    setEditingSceneId(null);
    setScenePrompt(''); setSceneBrief(''); resetSceneMedia();
  };

  const assembleFilm = async () => {
    if (!activeStoryboard || assembling) return;
    setAssembling(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/ia-studio/storyboard/${activeStoryboard.storyboard_id}/assemble`, {}, { headers: authHeaders },
      );
      if (res.data.success) await openStoryboard(activeStoryboard.storyboard_id);
    } catch (e) {
      window.alert(e?.response?.data?.error || "Échec de l'assemblage");
    }
    setAssembling(false);
  };

  // Paiement carte reussi cote Stripe (stripe.confirmPayment) - le credit reel se fait
  // ensuite cote serveur via le webhook /api/stripe/webhook (payment_intent.succeeded), donc
  // on relance loadCredits() a quelques secondes d'intervalle plutot qu'une seule fois pour
  // laisser le temps au webhook d'arriver.
  const handleCardTopupSuccess = () => {
    setShowTopup(false);
    window.alert('Paiement réussi ! Votre solde sera mis à jour dans quelques secondes.');
    loadCredits();
    setTimeout(loadCredits, 3000);
    setTimeout(loadCredits, 8000);
  };

  const submitTopup = async (amount, method, ref) => {
    if (!amount || !ref.trim() || submittingTopup) return;
    setSubmittingTopup(true);
    try {
      await axios.post(`${API_URL}/api/ia-studio/video/credits/order`, {
        amount_ar: parseInt(amount, 10), method, payment_ref: ref.trim(),
      }, { headers: authHeaders });
      setShowTopup(false);
      window.alert('Demande envoyée ! Votre solde sera crédité après vérification du paiement.');
    } catch (e) {
      window.alert(e?.response?.data?.error || 'Erreur');
    }
    setSubmittingTopup(false);
  };

  const providerCfg = providers[provider];
  const sceneCfg = providers[sceneProvider];

  // Tuiles du picker : les 3 tiers veo_* (config strictement identique cote backend) sont
  // regroupes en une seule tuile "veo_group" ("TeckiA V"), les autres providers restent
  // inchanges. Partage entre mode normal et storyboard (meme etat `providers`).
  const providerTiles = (() => {
    const tiles = [];
    let veoAdded = false;
    Object.entries(providers).forEach(([key, cfg]) => {
      if (isVeoTierKey(key)) {
        if (!veoAdded) {
          const defaultTierKey = VEO_TIERS.map(t => t.key).find(k => providers[k]);
          tiles.push({ key: 'veo_group', cfg: providers[defaultTierKey] });
          veoAdded = true;
        }
      } else {
        tiles.push({ key, cfg });
      }
    });
    return tiles;
  })();
  const scenes = activeStoryboard?.scenes || [];
  const completedScenesCount = scenes.filter(s => s.status === 'completed').length;
  const lastSceneQueued = scenes.length > 0 && scenes[scenes.length - 1].status === 'queued';

  // Cout estime cote client, meme formule que _video_price_for (backend) : forfait par
  // resolution pour omni (ignore la duree), sinon prix/seconde x duree pour les 3 autres.
  // Omni a un tarif majore si une video de reference est jointe (cout Kie plus eleve) -
  // voir cfg.pricing_with_video / _resolution_price_key cote backend.
  // Wan / Seedance 2.5 : le fournisseur facture (secondes de video fournie + secondes generees), et pour Wan « fournie + generee <= 30 s » (voir _video_price_for cote serveur).
  const effectiveGenDuration = (cfg, duration, refSeconds) => (
    cfg?.bills_ref_seconds && cfg.ref_total_cap && refSeconds > 0
      ? Math.max(cfg.min_duration || 1, Math.min(duration, Math.floor(cfg.ref_total_cap - refSeconds)))
      : duration
  );
  const estimatePrice = (cfg, resolution, duration, hasVideoRef, refSeconds = 0) => {
    if (!cfg || !cfg.pricing) return 0;
    const withVideo = hasVideoRef && cfg.pricing_with_video && cfg.pricing_with_video[resolution] != null;
    const rate = (withVideo ? cfg.pricing_with_video[resolution] : cfg.pricing[resolution]) || 0;
    if (cfg.flat_pricing) return rate;
    if (cfg.bills_ref_seconds && hasVideoRef && refSeconds > 0) return Math.ceil(rate * (effectiveGenDuration(cfg, duration, refSeconds) + refSeconds));
    return rate * duration;
  };
  const sumRefSeconds = (files) => files.filter(Boolean).reduce((t, f) => t + (f._dur || 0), 0);
  const normalRefSecs = sumRefSeconds([videoFile, ...refVideoFiles]);
  const sceneRefSecs = sumRefSeconds([sceneVideoFile, ...sceneRefVideoFiles]);
  const hasNormalVideoRef = !!videoFile || refVideoFiles.length > 0;
  const hasSceneVideoRef = !!sceneVideoFile || sceneRefVideoFiles.length > 0;
  const normalPrice = estimatePrice(providerCfg, resolution, duration, hasNormalVideoRef, normalRefSecs);
  const scenePrice = estimatePrice(sceneCfg, sceneResolution, sceneDuration, hasSceneVideoRef, sceneRefSecs);
  // Cout live du realisateur IA : recalcule des que le client change la resolution d'une scene
  // (voir updateSceneResolution) - meme formule que le serveur (_film_script_cost), les
  // personnages ne dependant pas de la resolution restent sur l'estimation initiale (filmCost).
  const filmLiveScenesCost = (filmScript?.scenes || []).filter(s => s.validated).reduce((sum, s) => sum + estimatePrice(providers[s.engine], s.resolution, s.duration, false), 0);
  const filmGeneratedCharsCount = (filmScript?.characters || []).filter(c => (c.image_mode || (c.image_url ? 'upload' : 'generate')) === 'generate').length;
  const filmLiveCharsCost = filmGeneratedCharsCount * (filmCost?.character_price_unit || 0);
  const filmLiveTotal = filmLiveCharsCost + filmLiveScenesCost;

  // ---- Mode flux : verification / validation scene par scene, import d'un film deja prepare
  const filmValidatedCount = (filmScript?.scenes || []).filter(x => x.validated).length;
  const pushFilmSync = async (updates) => {
    if (!filmStoryboardId || !updates.length) return;
    try {
      await axios.post(`${API_URL}/api/ia-studio/video/film/${filmStoryboardId}/sync`, { updates }, { headers: authHeaders });
      axios.post(`${API_URL}/api/ia-studio/video/film/${filmStoryboardId}/step`, {}, { headers: authHeaders }).catch(() => {});
    } catch (e) { setFilmNotice(e?.response?.data?.error || 'Synchronisation impossible, réessayez.'); }
  };
  const toggleFilmValidate = (i) => {
    if (!filmScript?.scenes?.[i]) return;
    const v = !filmScript.scenes[i].validated;
    const nextScenes = filmScript.scenes.map((x, k) => (k === i ? { ...x, validated: v } : x));
    setFilmScript({ ...filmScript, scenes: nextScenes });
    pushFilmSync([{ index: i, scene: nextScenes[i], validated: v }]);
  };
  const setAllFilmValidated = (v) => {
    if (!filmScript?.scenes) return;
    const open = (k) => !filmStatus?.nodes || ['draft', 'ready', 'waiting'].includes(filmStatus.nodes[k]?.state);
    const nextScenes = filmScript.scenes.map((x, k) => (open(k) ? { ...x, validated: v } : x));
    setFilmScript({ ...filmScript, scenes: nextScenes });
    pushFilmSync(nextScenes.map((x, k) => ({ index: k, scene: x, validated: x.validated })).filter((_, k) => open(k)));
  };
  const toggleFilmChainFlow = (i) => {
    if (i === 0 || !filmScript?.scenes?.[i]) return;
    const v = !filmScript.scenes[i].uses_previous_frame;
    toggleFilmChain(i);
    pushFilmSync([{ index: i, chain: v }]);
  };
  const saveFilmScene = (i) => {
    if (!filmScript?.scenes?.[i]) return;
    pushFilmSync([{ index: i, scene: filmScript.scenes[i], validated: !!filmScript.scenes[i].validated }]);
    setFilmNotice(`Scène ${i + 1} enregistrée.`);
  };
  const importFilm = async () => {
    if (filmImportText.trim().length < 80 || filmImporting) return;
    setFilmImporting(true);
    setFilmScriptError(null);
    try {
      const res = await axios.post(`${API_URL}/api/ia-studio/video/film/import`, { text: filmImportText, language: filmLanguage, aspect_ratio: filmAspectRatio }, { headers: authHeaders });
      if (res.data.success) {
        setFilmScript(res.data.script);
        setFilmCost(res.data.cost);
        setFilmPrompts(res.data.prompts || []);
        setFilmSummaryMg(null); setFilmSummaryLang('fr'); setFilmUndo(null); setFilmNotice('');
        setFilmSel(null);
        setFilmStage('script');
      } else setFilmScriptError(res.data.error || "Import impossible, réessayez.");
    } catch (e) { setFilmScriptError(e?.response?.data?.error || "Import impossible, réessayez."); }
    setFilmImporting(false);
  };
  const fixFilmNode = async (mode) => {
    const fs = filmStatus?.failed_scenes?.[String(filmSel)];
    if (!fs || filmFixBusy || !filmStoryboardId) return;
    setFilmFixBusy(mode); setFilmFixNote('');
    try {
      const body = { index: fs.index, mode, ...(mode === 'ai' ? { instruction: filmFixInstr.trim() } : {}), ...(mode === 'text' ? { prompt: filmFixText } : {}) };
      const res = await axios.post(`${API_URL}/api/ia-studio/video/film/${filmStoryboardId}/fix-node`, body, { headers: authHeaders });
      if (res.data.success) {
        setFilmFixNote(res.data.changes ? `✅ Scène relancée. ${res.data.changes}` : '✅ Scène relancée.');
        setFilmFixInstr('');
        loadCredits();
        pollFilmStatus(filmStoryboardId);
      } else setFilmFixNote(res.data.message || res.data.error || 'Correction impossible.');
    } catch (e) {
      const d = e?.response?.data;
      setFilmFixNote(d?.message || d?.error || 'Correction impossible, réessayez.');
    }
    setFilmFixBusy('');
  };

  const renderFilmFixPanel = (fs, fixFn) => {
    const changed = filmFixText.trim() !== (fs.prompt_final || '').trim();
    return (
      <div className="rounded-xl border border-fuchsia-400/30 bg-fuchsia-500/5 p-3 space-y-3">
        <div className="text-xs font-bold text-fuchsia-100">🛠 Corriger cette scène et la relancer</div>
        <p className="text-[10px] text-gray-400 leading-snug">
          {fs.moderation ? "Le filtre de sécurité du moteur se trompe souvent (mots comme moquerie, pleurs, bagarre, enfants en difficulté…). " : "Le moteur a refusé ou n'a pas pu générer cette scène. "}
          Vos crédits sont déjà remboursés ; la relance coûte {formatAr(fs.price)}. Le reste du film est conservé.
        </p>
        <div className="space-y-1.5">
          <label className="block text-[10px] font-bold text-fuchsia-200">✨ Laisser l&apos;IA corriger (vous pouvez lui dire quoi changer)</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input value={filmFixInstr} onChange={e => setFilmFixInstr(e.target.value)} maxLength={500}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); fixFn('ai'); } }}
              placeholder="Facultatif. Ex : garde la moquerie mais en taquinerie amicale, personne n'est humilié"
              className="flex-1 min-w-0 bg-black/30 border border-white/10 rounded-lg px-2.5 py-2 text-[11px] text-white placeholder-gray-500 focus:outline-none focus:border-fuchsia-300" />
            <button onClick={() => fixFn('ai')} disabled={!!filmFixBusy} className="px-3.5 py-2 rounded-lg text-[11px] font-bold text-white flex items-center justify-center gap-1.5 disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #9333EA, #DB2777)' }}>
              {filmFixBusy === 'ai' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Corriger avec l&apos;IA et relancer
            </button>
          </div>
        </div>
        <div className="space-y-1.5">
          <label className="block text-[10px] font-bold text-gray-300">✍️ Ou modifier vous-même le texte envoyé au moteur (anglais, dialogues entre guillemets)</label>
          {fs.prompt_fr && <p className="text-[10px] text-gray-400 leading-snug">En français : {fs.prompt_fr}</p>}
          <textarea value={filmFixText} onChange={e => setFilmFixText(e.target.value)} rows={8}
            className="w-full bg-black/30 border border-white/10 rounded-lg px-2.5 py-2 text-[10px] leading-relaxed text-gray-200 font-mono focus:outline-none focus:border-purple-400 resize-y" />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => fixFn('text')} disabled={!!filmFixBusy || !changed || filmFixText.trim().length < 20}
              className="px-3.5 py-2 rounded-lg text-[11px] font-bold text-white bg-purple-600/40 hover:bg-purple-600/60 border border-purple-400/40 flex items-center gap-1.5 disabled:opacity-40">
              {filmFixBusy === 'text' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Relancer avec mon texte
            </button>
            <button onClick={() => fixFn('retry')} disabled={!!filmFixBusy}
              className="px-3.5 py-2 rounded-lg text-[11px] font-bold text-gray-200 bg-white/5 hover:bg-white/10 border border-white/10 flex items-center gap-1.5 disabled:opacity-40">
              {filmFixBusy === 'retry' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Réessayer sans rien changer
            </button>
          </div>
        </div>
        {filmFixNote && <div className={`text-[11px] rounded-lg px-3 py-2 border ${filmFixNote.startsWith('✅') ? 'text-emerald-100 bg-emerald-500/10 border-emerald-400/30' : 'text-red-200 bg-red-500/10 border-red-400/30'}`}>{filmFixNote}</div>}
      </div>
    );
  };

  const updateSceneDuration = (idx, duration) => setFilmScript(prev => ({ ...prev, scenes: prev.scenes.map((x, i) => (i === idx ? { ...x, duration } : x)) }));
  // Glisser-déposer / flèches : change l'ordre des scènes AVANT le lancement. Une scène dont la scène d'avant change n'est plus une « suite » (elle devient une nouvelle scène).
  const moveFilmSceneTo = (from, to) => {
    if (!filmScript || filmStoryboardId || from === to || to < 0 || to >= filmScript.scenes.length) return;
    setFilmUndo(filmScript);
    const list = filmScript.scenes.map((x, k) => ({ ...x, _k: k }));
    const [m] = list.splice(from, 1);
    list.splice(to, 0, m);
    const moved = list.map((x, k) => {
      const { _k, ...rest } = x;
      const changed = (_k - 1) !== (k > 0 ? list[k - 1]._k : -1);
      return changed ? { ...rest, uses_previous_frame: false, prompt_override: '', bridge: '', transition_in: k > 0 ? 'fadeblack' : '' } : rest;
    });
    const next = { ...filmScript, scenes: renumberScenes(moved) };
    setFilmScript(next);
    setFilmSel(sel => (sel === from ? to : sel));
    refreshFilmPreview(next);
    setFilmNotice(`Scène ${from + 1} déplacée en position ${to + 1}. Les enchaînements touchés sont devenus des « nouvelles scènes » : vérifiez-les.`);
  };
  const reorderFilmScenes = (from, target, after) => {
    let t = after ? target + 1 : target;
    if (from < t) t -= 1;
    moveFilmSceneTo(from, t);
  };

  const renderFilmModal = () => {
    if (mode !== 'film' || filmSel === null || !filmScript?.scenes) return null;
    const inProd = filmStage === 'production' && filmStatus?.graph;
    const nodes = inProd ? (filmStatus.nodes || []) : null;
    const scenes = inProd && filmScript.scenes.length !== nodes.length ? (filmStatus.plan_scenes || []) : filmScript.scenes;
    const sc = scenes[filmSel];
    if (!sc) return null;
    const nd = nodes ? nodes[filmSel] : null;
    const open = !nd || ['draft', 'ready', 'waiting'].includes(nd.state);
    const fs = nd?.state === 'failed' ? filmStatus.failed_scenes?.[String(filmSel)] : null;
    return (
      <FilmSceneModal
        scene={sc}
        index={filmSel}
        total={scenes.length}
        node={nd}
        providers={providers}
        formatAr={formatAr}
        priceOf={(x, o = {}) => estimatePrice(providers[x.engine], o.resolution || x.resolution, o.duration || x.duration, false)}
        aspect={filmScript.aspect_ratio || filmAspectRatio}
        locked={!open}
        canReorder={!filmStoryboardId}
        canStructure={!filmStoryboardId}
        showSave={!!filmStoryboardId}
        explainLang={filmExplainLang}
        translating={filmTranslating[filmSel]}
        aiBusy={filmAiBusy}
        aiText={filmAi[filmSel] || ''}
        filmPrompt={filmPrompts[filmSel] || ''}
        characterNames={sc.character_names || []}
        fixPanel={fs ? renderFilmFixPanel(fs, fixFilmNode) : null}
        failedNote={filmStatus?.node_notes?.[String(filmSel)] || ''}
        onClose={() => setFilmSel(null)}
        onPrev={() => setFilmSel(Math.max(0, filmSel - 1))}
        onNext={() => setFilmSel(Math.min(scenes.length - 1, filmSel + 1))}
        onMove={(d) => moveFilmSceneTo(filmSel, filmSel + d)}
        onField={(f, v) => updateSceneField(filmSel, f, v)}
        onDialogue={(di, k, v) => updateDialogueLine(filmSel, di, k, v)}
        onRefresh={() => refreshFilmPreview()}
        onResolution={(r) => updateSceneResolution(filmSel, r)}
        onDuration={(d) => updateSceneDuration(filmSel, d)}
        onToggleChain={() => toggleFilmChainFlow(filmSel)}
        onToggleValidate={() => toggleFilmValidate(filmSel)}
        onSave={() => saveFilmScene(filmSel)}
        onTranslate={(dir) => translateScene(filmSel, dir)}
        onAiText={(v) => setFilmAi(p => ({ ...p, [filmSel]: v }))}
        onAiEdit={(m) => aiEditScene(filmSel, m)}
        onDelete={() => deleteFilmScene(filmSel)}
        onOverride={(v) => updateSceneField(filmSel, 'prompt_override', v)}
        onOverrideReset={() => { updateSceneField(filmSel, 'prompt_override', ''); setTimeout(() => refreshFilmPreview(), 50); }}
      />
    );
  };

  const renderFlowProduction = () => {
    const nodes = filmStatus.nodes || [];
    const flowScenes = (filmScript?.scenes?.length === nodes.length && filmScript.scenes.length) ? filmScript.scenes : (filmStatus.plan_scenes || []);
    const draftLeft = nodes.filter(x => x.state === 'draft').length;
    return (
      <div>
        <h3 className="text-white font-bold text-sm mb-2.5 flex items-center gap-1.5">
          <Clapperboard className="w-4 h-4 text-purple-300" /> Production · flux des scènes
          <span className="text-gray-500 font-normal">({nodes.filter(x => x.state === 'completed').length}/{nodes.length} terminées)</span>
        </h3>
        <FilmFlow
          scenes={flowScenes}
          nodes={nodes}
          providers={providers}
          priceOf={(sc) => estimatePrice(providers[sc.engine], sc.resolution, sc.duration, false)}
          formatAr={formatAr}
          selected={filmSel}
          onSelect={(i) => setFilmSel(i)}
          onToggleValidate={toggleFilmValidate}
          onToggleChain={toggleFilmChainFlow}
          final={{ done: filmStatus.status === 'assembled', montage: filmStatus.status === 'montage' }}
          notes={filmStatus.node_notes || {}}
        />
        {draftLeft > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-amber-100 bg-amber-500/10 border border-amber-400/30 rounded-xl px-3 py-2">
            <span className="flex-1">{draftLeft} scène{draftLeft > 1 ? 's' : ''} attend{draftLeft > 1 ? 'ent' : ''} votre validation : elle{draftLeft > 1 ? 's partent' : ' part'} dès que vous cliquez « Valider ».</span>
            <button onClick={() => setAllFilmValidated(true)} className="px-3 py-1.5 rounded-lg font-bold bg-emerald-600/30 hover:bg-emerald-600/50 border border-emerald-400/40 text-emerald-100">✔ Tout valider</button>
          </div>
        )}
      </div>
    );
  };

  const structuredJoined = [fieldEnvironment, fieldAmbiance, fieldCharacter, fieldOutfit, fieldMovement, fieldAction, fieldInfo].filter(s => s.trim()).join(' ');
  const effectivePrompt = promptMode === 'structured' ? structuredJoined : prompt;
  const sceneStructuredJoined = [sceneFieldEnvironment, sceneFieldAmbiance, sceneFieldCharacter, sceneFieldOutfit, sceneFieldMovement, sceneFieldAction, sceneFieldInfo].filter(s => s.trim()).join(' ');
  const sceneEffectivePrompt = scenePromptMode === 'structured' ? sceneStructuredJoined : scenePrompt;

  return (
    <div className="min-h-screen bg-[#07050d] pb-16 relative">
      <div className="pointer-events-none fixed -top-32 -left-32 w-72 h-72 bg-purple-700/10 rounded-full blur-3xl" />
      <div className="pointer-events-none fixed top-32 -right-32 w-72 h-72 bg-fuchsia-700/[0.07] rounded-full blur-3xl" />

      <div className="sticky top-0 z-40 bg-[#07050d]/95 backdrop-blur-md border-b border-white/5">
        <div className={`mx-auto px-4 pt-4 pb-3 transition-all duration-300 ${mode === 'storyboard' ? 'max-w-7xl' : 'max-w-5xl'}`}>
          <div className="flex items-center justify-between mb-3">
            <button onClick={onBack} className="flex items-center gap-2 text-white/70 hover:text-white transition text-sm">
              <ArrowLeft className="w-4 h-4" /> Retour
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowMyStats(true)}
                title="Mes statistiques"
                className="flex items-center justify-center w-8 h-8 rounded-full border bg-white/5 text-gray-300 border-white/10 hover:border-amber-400/50 hover:text-amber-300 transition"
              >
                <BarChart3 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setShowTopup(true)}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border bg-white/5 text-gray-300 border-white/10 hover:border-purple-400/50 transition"
              >
                <Coins className="w-3.5 h-3.5 text-amber-300" /> {credits.toLocaleString('fr-FR')} Ar
              </button>
            </div>
          </div>

          <h1 className="text-xl font-black tracking-tight mb-3 flex items-center justify-center gap-2">
            <Film className="w-5 h-5 text-purple-400 drop-shadow-[0_0_10px_rgba(168,85,247,0.6)]" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-200 via-fuchsia-200 to-purple-200">TeckiA IA Studio Vidéo</span>
          </h1>

          <div className="flex gap-2 bg-white/5 border border-white/10 rounded-xl p-1">
            <button
              onClick={() => setMode('normal')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition ${mode === 'normal' ? 'bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white shadow-[0_0_16px_rgba(147,51,234,0.4)]' : 'text-gray-400 hover:text-gray-200'}`}
            >
              <Wand2 className="w-4 h-4" /> Génération normale
            </button>
            <button
              onClick={() => setMode('storyboard')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition ${mode === 'storyboard' ? 'bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white shadow-[0_0_16px_rgba(147,51,234,0.4)]' : 'text-gray-400 hover:text-gray-200'}`}
            >
              <Clapperboard className="w-4 h-4" /> Storyboard
            </button>
            <button
              onClick={() => setMode('film')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition ${mode === 'film' ? 'bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white shadow-[0_0_16px_rgba(147,51,234,0.4)]' : 'text-gray-400 hover:text-gray-200'}`}
            >
              <Sparkles className="w-4 h-4" /> Création / narration
            </button>
            <button
              onClick={() => setMode('pub')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition ${mode === 'pub' ? 'bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white shadow-[0_0_16px_rgba(147,51,234,0.5)]' : 'text-gray-400 hover:text-white'}`}
            >
              <Megaphone className="w-4 h-4" /> TeckiA Pub
            </button>
          </div>
        </div>
      </div>

      <div className={`mx-auto px-4 pt-6 relative transition-all duration-300 ${mode === 'storyboard' ? 'max-w-7xl' : 'max-w-5xl'}`}>
        {mode === 'normal' && (
          <div className="grid lg:grid-cols-[300px_1fr] gap-5 items-start">
            {/* Colonne gauche : vidéos générées, toujours visible, défile indépendamment du formulaire */}
            <div className="lg:sticky lg:top-44 order-2 lg:order-1">
              <div className="relative bg-[#140a24]/95 border border-purple-500/25 rounded-2xl p-4 shadow-[0_0_40px_rgba(147,51,234,0.15)] mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-white font-bold text-sm flex items-center gap-1.5">
                    <Film className="w-4 h-4 text-purple-300" /> Vos vidéos
                  </div>
                  <button onClick={loadHistory} className="text-gray-400 hover:text-white transition hover:rotate-180 duration-500">
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>

                {currentJob && (
                  <div className="mb-4 text-center">
                    {currentJob.status === 'queued' && (
                      <>
                        <TeckiALoader subtitle="Ça peut prendre 1 à 5 minutes selon le style choisi" />
                        <button onClick={cancelGeneration} className="text-xs text-gray-400 hover:text-red-300 transition -mt-4 mb-2">Annuler</button>
                      </>
                    )}
                    {currentJob.status === 'failed' && <div className="py-4 text-red-300 text-sm">{currentJob.error}</div>}
                    {currentJob.status === 'completed' && (
                      <>
                        <video src={currentJob.video_url} controls className="w-full rounded-xl mb-3" />
                        <a
                          href={currentJob.video_url} download target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition hover:scale-105"
                        >
                          <Download className="w-3.5 h-3.5" /> Télécharger
                        </a>
                      </>
                    )}
                  </div>
                )}

                <div className="max-h-[55vh] overflow-y-auto pr-1 space-y-3">
                  {loadingHistory ? (
                    <div className="text-center text-gray-400 text-sm py-8">Chargement...</div>
                  ) : history.length === 0 ? (
                    <div className="text-center text-gray-400 text-sm py-8 flex flex-col items-center gap-2">
                      <Film className="w-8 h-8 text-gray-700" /> Aucune vidéo générée pour l&apos;instant.
                    </div>
                  ) : (
                    history.map(item => (
                      <div key={item.job_id} className="relative rounded-xl overflow-hidden border border-white/10">
                        <video src={item.video_url} controls className="w-full aspect-video object-contain bg-black" />
                      </div>
                    ))
                  )}
                </div>
              </div>
              <Retention14DaysNotice />
            </div>

            {/* Colonne droite : formulaire de génération, reste fixe pendant que la colonne gauche défile */}
            <div className="order-1 lg:order-2">
            <div className="relative bg-[#140a24]/95 border border-purple-500/15 rounded-2xl p-5 mb-6 shadow-[0_0_40px_rgba(147,51,234,0.08)]">
              <label className="block text-white font-semibold mb-2 text-sm">Style</label>
              <div className="grid grid-cols-2 gap-2 mb-4">
                {providerTiles.map(({ key, cfg }) => {
                  const active = key === 'veo_group' ? isVeoTierKey(provider) : provider === key;
                  const label = key === 'veo_group' ? 'TeckiA V' : cfg.label;
                  const info = key === 'veo_group' ? VEO_GROUP_INFO : cfg.info;
                  const theme = providerTheme(key);
                  const Icon = theme.icon;
                  return (
                    <div key={key} className="relative group" style={{ perspective: '700px' }}>
                      {key === 'seedance_mini' && (
                        <span className="absolute -top-2 right-2 z-10 px-1.5 py-0.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[9px] font-bold shadow-[0_0_8px_rgba(16,185,129,0.6)]">
                          ⭐ Meilleur choix
                        </span>
                      )}
                      <button
                        onClick={() => onProviderTileClick(key)}
                        className={`relative w-full overflow-hidden px-3 py-2.5 rounded-xl text-xs font-bold border text-left transition-all duration-300 group-hover:-translate-y-0.5 ${
                          active
                            ? `bg-gradient-to-br ${theme.color} border-white/40 text-white ${theme.glow}`
                            : 'bg-white/5 border-white/10 text-gray-300 hover:border-white/25'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center bg-gradient-to-br ${theme.color} ${active ? 'shadow-[inset_0_1px_1px_rgba(255,255,255,0.35)]' : ''}`}>
                            <Icon className="w-3.5 h-3.5 text-white" />
                          </div>
                          <div className="min-w-0">
                            <div className="truncate">{label}</div>
                            <div className="text-[10px] font-normal opacity-70 mt-0.5">jusqu'à {cfg.max_duration}s</div>
                          </div>
                        </div>
                      </button>
                      {info && (
                        <div className="pointer-events-none absolute z-20 left-0 right-0 top-full mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-150">
                          <div className="bg-[#1c0f30] border border-purple-500/30 rounded-xl p-3 text-[11px] text-gray-300 leading-relaxed shadow-2xl">
                            {info}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-2 mb-4 bg-black/20 border border-white/10 rounded-xl p-1">
                <button
                  onClick={() => setPromptMode('free')}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition ${promptMode === 'free' ? 'bg-purple-600 text-white shadow-[0_0_12px_rgba(147,51,234,0.4)]' : 'text-gray-400 hover:text-gray-200'}`}
                >
                  <MessageSquare className="w-3.5 h-3.5" /> Prompt libre
                </button>
                <button
                  onClick={() => setPromptMode('structured')}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition ${promptMode === 'structured' ? 'bg-purple-600 text-white shadow-[0_0_12px_rgba(147,51,234,0.4)]' : 'text-gray-400 hover:text-gray-200'}`}
                >
                  <SlidersHorizontal className="w-3.5 h-3.5" /> Champs détaillés
                </button>
              </div>

              {promptMode === 'free' ? (
              <>
              <label className="flex items-center gap-1.5 text-white font-semibold mb-2 text-sm">
                Que voulez-vous ? <span className="text-[10px] font-normal text-gray-500">(décrivez librement, TeckiA rédige le prompt)</span>
              </label>
              <div className="flex gap-2 mb-4">
                <textarea
                  value={brief}
                  onChange={e => setBrief(e.target.value)}
                  rows={2}
                  placeholder="Ex : une pub pour mon parfum, ambiance luxueuse, coucher de soleil..."
                  className="flex-1 bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-400 resize-y min-h-[76px] transition"
                />
                <button
                  onClick={generatePromptFromBrief}
                  disabled={!brief.trim() || generatingPrompt}
                  title="Générer le prompt"
                  className="flex-shrink-0 w-11 rounded-xl bg-purple-600 hover:bg-purple-500 disabled:opacity-40 flex items-center justify-center transition"
                >
                  {generatingPrompt ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <MessageSquarePlus className="w-4 h-4 text-white" />}
                </button>
              </div>

              <label className="block text-white font-semibold mb-2 text-sm">Prompt final</label>
              <textarea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                rows={3}
                placeholder="Ex : Un flacon de parfum qui tourne lentement sur un rocher au coucher du soleil..."
                className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-400 focus:shadow-[0_0_0_3px_rgba(147,51,234,0.15)] resize-y min-h-[76px] transition"
              />
              <OmniDurationWarning provider={provider} text={prompt} />
              </>
              ) : (
              <div className="mb-4">
                <div className="flex items-center gap-1.5 text-white font-semibold mb-3 text-sm">
                  <SlidersHorizontal className="w-3.5 h-3.5 text-purple-300" />
                  Champs détaillés
                  <span className="text-[10px] font-normal text-gray-500 ml-1">— remplissez ce qui compte, laissez le reste vide</span>
                </div>
                <div className="space-y-3">
                  {STRUCTURED_FIELDS.map(f => {
                    const value = { environment: fieldEnvironment, ambiance: fieldAmbiance, character: fieldCharacter, outfit: fieldOutfit, movement: fieldMovement, action: fieldAction, info: fieldInfo }[f.key];
                    const setter = structuredSetters[f.key];
                    return (
                      <div key={f.key}>
                        <label className="block text-gray-400 text-xs font-semibold mb-1">{f.label}</label>
                        <div className="flex items-center gap-1.5">
                          <input
                            value={value}
                            onChange={e => setter(e.target.value)}
                            onFocus={() => setStructuredFocus(f.key)}
                            placeholder={f.placeholder}
                            className="w-full bg-black/30 border border-white/10 rounded-xl px-3.5 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-400 transition"
                          />
                          <button
                            type="button"
                            onClick={() => openFieldImagePicker(f.key)}
                            title={`Attacher une image à ${f.label}`}
                            disabled={refFiles.length >= (providerCfg?.max_images || 7)}
                            className="flex-shrink-0 w-9 h-9 rounded-xl border border-white/10 bg-white/5 hover:bg-purple-600/30 hover:border-purple-400/50 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-gray-400 hover:text-purple-300 transition"
                          >
                            <Paperclip className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <input
                  type="file" accept="image/*" multiple ref={fieldFileInputRef} className="hidden"
                  onChange={e => {
                    if (pendingFieldKey) attachImageToField(pendingFieldKey, e.target.files);
                    e.target.value = '';
                    setPendingFieldKey(null);
                  }}
                />
              </div>
              )}

              {providerCfg && isVeoTierKey(provider) && (
                <div className="mt-4">
                  <label className="block text-white font-semibold mb-2 text-sm">Qualité TeckiA V</label>
                  <div className="flex gap-1.5">
                    {VEO_TIERS.filter(t => providers[t.key]).map(t => (
                      <button
                        key={t.key}
                        onClick={() => onTierChange(t.key)}
                        className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold border transition ${
                          provider === t.key
                            ? 'bg-gradient-to-r from-purple-600 to-fuchsia-600 border-purple-400 text-white shadow-[0_0_16px_rgba(147,51,234,0.5)]'
                            : 'bg-white/5 border-white/10 text-gray-300 hover:border-white/25'
                        }`}
                      >
                        {t.short}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {providerCfg && (
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div>
                    <label className="block text-white font-semibold mb-2 text-sm">Résolution</label>
                    <div className="flex flex-wrap gap-1.5">
                      {providerCfg.resolutions.map(r => (
                        <button
                          key={r} onClick={() => {
                            setResolution(r);
                            // Certaines resolutions (360p Omni via fal.ai) ne supportent pas de
                            // video de reference - on vide la selection pour eviter qu'elle soit
                            // silencieusement ignoree cote backend.
                            if (providerCfg.video_ref_unsupported_resolutions?.includes(r)) {
                              setVideoFile(null); setVideoPreview(null); setRefVideoFiles([]);
                            }
                          }}
                          className={`px-2.5 py-1.5 rounded-lg text-xs font-bold border transition ${resolution === r ? 'bg-purple-600 border-purple-400 text-white' : 'bg-white/5 border-white/10 text-gray-400'}`}
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-white font-semibold mb-2 text-sm">Durée : {duration}s</label>
                    {provider === 'omni' ? (
                      <p className="text-[11px] text-gray-500 mt-1.5">Durée fixe (même prix quelle que soit la durée choisie).</p>
                    ) : providerCfg?.requires_audio ? (
                      <p className="text-[11px] text-gray-500 mt-1.5">Suit la durée de l'audio fourni (max {providerCfg?.max_duration}s).</p>
                    ) : providerCfg?.requires_video ? (
                      <p className="text-[11px] text-gray-500 mt-1.5">Suit la durée de la vidéo fournie (max {providerCfg?.max_duration}s).</p>
                    ) : (
                      <>
                        <input
                          type="range" min={1} max={providerCfg.max_duration} value={duration}
                          onChange={e => setDuration(parseInt(e.target.value, 10))}
                          className="w-full accent-purple-500"
                          disabled={!!videoFile}
                        />
                        {videoFile && <p className="text-[10px] text-gray-500 mt-1">Ignorée : la durée suit la vidéo fournie</p>}
                      </>
                    )}
                  </div>
                </div>
              )}

              {providerCfg && providerCfg.aspect_ratios.length > 1 && (
                <div className="mt-4">
                  <label className="block text-white font-semibold mb-2 text-sm">Format</label>
                  <div className="flex flex-wrap gap-1.5">
                    {providerCfg.aspect_ratios.map(ar => (
                      <button
                        key={ar} onClick={() => setAspectRatio(ar)}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-bold border transition ${aspectRatio === ar ? 'bg-purple-600 border-purple-400 text-white' : 'bg-white/5 border-white/10 text-gray-400'}`}
                      >
                        {ar}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {providerCfg?.supports_generate_audio && (
                <div className="mt-4 flex items-center justify-between bg-black/20 border border-white/10 rounded-xl px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Music2 className="w-4 h-4 text-purple-300" />
                    <span className="text-white text-sm font-semibold">Générer l'audio automatiquement</span>
                  </div>
                  <button
                    onClick={() => setGenerateAudio(v => !v)}
                    className={`w-11 h-6 rounded-full transition relative flex-shrink-0 ${generateAudio ? 'bg-purple-600' : 'bg-white/10'}`}
                  >
                    <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${generateAudio ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              )}

              <div className="mt-4">
                <label className="block text-white font-semibold mb-2 text-sm">
                  Photo{providerCfg?.max_images > 1 ? 's de référence' : ''} {providerCfg?.requires_image ? '(requis)' : `(optionnel, jusqu'à ${providerCfg?.max_images || 7})`}
                </label>
                {refPreviews.length > 0 && (
                  <p className="text-[11px] text-gray-500 mb-2">
                    Touchez une photo pour l'insérer {promptMode === 'structured' ? (
                      <>dans le champ <span className="text-purple-300">{STRUCTURED_FIELDS.find(f => f.key === structuredFocus)?.label}</span></>
                    ) : 'dans le prompt'} (ex: <span className="text-purple-300">@image1</span>)
                  </p>
                )}
                <div className="flex flex-wrap gap-3">
                  {refPreviews.map((src, idx) => (
                    <button
                      key={idx} type="button" onClick={() => insertImageTag(idx + 1)}
                      className="relative w-20 h-20 rounded-xl overflow-hidden border border-purple-500/25 hover:border-purple-400 transition group"
                    >
                      <img src={src} alt="" className="w-full h-full object-cover" />
                      <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-center py-0.5">
                        <span className="text-[9px] font-bold text-purple-300">@image{idx + 1}</span>
                      </div>
                      <div
                        role="button"
                        onClick={e => { e.stopPropagation(); removeRef(idx); }}
                        className="absolute top-1 right-1 w-5 h-5 bg-black/70 hover:bg-red-600 rounded-full flex items-center justify-center"
                      >
                        <X className="w-3 h-3 text-white" />
                      </div>
                    </button>
                  ))}
                  {refFiles.length < (providerCfg?.max_images || 7) && (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-20 h-20 rounded-xl border-2 border-dashed border-white/20 flex flex-col items-center justify-center text-gray-400 hover:text-purple-300 hover:border-purple-400/50 transition"
                    >
                      <Upload className="w-5 h-5 mb-1" />
                      <span className="text-[10px]">Ajouter</span>
                    </button>
                  )}
                  <input
                    ref={fileInputRef} type="file" accept="image/*" multiple className="hidden"
                    onChange={e => { addRefFiles(e.target.files); e.target.value = ''; }}
                  />
                </div>
              </div>

              {providerCfg?.supports_video_ref && providerCfg.max_videos === 1
                && !providerCfg.video_ref_unsupported_resolutions?.includes(resolution) && (
                <div className="mt-4">
                  <label className="flex items-center gap-1.5 text-white font-semibold mb-2 text-sm">
                    <Video className="w-4 h-4 text-purple-300" /> Vidéo de référence {providerCfg.requires_video ? `(requis, max ${providerCfg.max_duration}s)` : '(optionnel)'}
                  </label>
                  <p className="text-[11px] text-gray-500 mb-2">
                    {providerCfg.requires_video
                      ? "Vidéo de mouvement/performance à reproduire sur votre photo."
                      : "Transforme une vidéo existante selon votre prompt, au lieu de partir de zéro."}
                  </p>
                  {videoPreview ? (
                    <div className="relative inline-block">
                      <video src={videoPreview} className="w-40 h-24 rounded-xl object-contain bg-black border border-purple-500/25" muted />
                      <button onClick={removeVideo} className="absolute top-1 right-1 w-6 h-6 bg-black/70 hover:bg-red-600 rounded-full flex items-center justify-center">
                        <X className="w-3.5 h-3.5 text-white" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => videoInputRef.current?.click()}
                      className="w-40 h-24 rounded-xl border-2 border-dashed border-white/20 flex flex-col items-center justify-center text-gray-400 hover:text-purple-300 hover:border-purple-400/50 transition"
                    >
                      <Video className="w-5 h-5 mb-1" />
                      <span className="text-[10px]">Ajouter une vidéo</span>
                    </button>
                  )}
                  <input
                    ref={videoInputRef} type="file" accept="video/*" className="hidden"
                    onChange={e => { onVideoPicked(e.target.files?.[0]); e.target.value = ''; }}
                  />
                </div>
              )}

              {providerCfg?.supports_first_last_frame && (
                <div className="mt-4">
                  <label className="flex items-center gap-1.5 text-white font-semibold mb-2 text-sm">
                    <ImagePlus className="w-4 h-4 text-purple-300" /> Première / dernière image (optionnel)
                  </label>
                  <div className="flex gap-4">
                    {[{ key: 'first', label: 'Première', preview: firstFramePreview, ref: firstFrameInputRef },
                      { key: 'last', label: 'Dernière', preview: lastFramePreview, ref: lastFrameInputRef }].map(f => (
                      <div key={f.key}>
                        {f.preview ? (
                          <div className="relative w-20 h-20 rounded-xl overflow-hidden border border-purple-500/25">
                            <img src={f.preview} alt="" className="w-full h-full object-cover" />
                            <button onClick={() => removeFrame(f.key)} className="absolute top-1 right-1 w-5 h-5 bg-black/70 hover:bg-red-600 rounded-full flex items-center justify-center">
                              <X className="w-3 h-3 text-white" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => f.ref.current?.click()}
                            className="w-20 h-20 rounded-xl border-2 border-dashed border-white/20 flex flex-col items-center justify-center text-gray-400 hover:text-purple-300 hover:border-purple-400/50 transition"
                          >
                            <Upload className="w-4 h-4 mb-1" />
                            <span className="text-[9px]">{f.label}</span>
                          </button>
                        )}
                        <input
                          ref={f.ref} type="file" accept="image/*" className="hidden"
                          onChange={e => { onFramePicked(f.key, e.target.files?.[0]); e.target.value = ''; }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {providerCfg?.supports_video_ref && providerCfg.max_videos > 1 && (
                <div className="mt-4">
                  <label className="flex items-center gap-1.5 text-white font-semibold mb-2 text-sm">
                    <Video className="w-4 h-4 text-purple-300" /> Vidéos de référence (optionnel, jusqu'à {providerCfg.max_videos})
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {refVideoFiles.map((f, idx) => (
                      <div key={idx} className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-gray-300">
                        <Video className="w-3.5 h-3.5 text-purple-300" /> {f.name.slice(0, 18)}
                        <button onClick={() => removeRefVideo(idx)}><X className="w-3 h-3 text-gray-400 hover:text-red-400" /></button>
                      </div>
                    ))}
                    {refVideoFiles.length < providerCfg.max_videos && (
                      <button
                        onClick={() => refVideoInputRef.current?.click()}
                        className="flex items-center gap-1.5 border-2 border-dashed border-white/20 rounded-lg px-3 py-1.5 text-xs text-gray-400 hover:text-purple-300 hover:border-purple-400/50 transition"
                      >
                        <Upload className="w-3.5 h-3.5" /> Ajouter
                      </button>
                    )}
                  </div>
                  <input
                    ref={refVideoInputRef} type="file" accept="video/*" multiple className="hidden"
                    onChange={e => { addRefVideoFiles(e.target.files, providerCfg.max_videos); e.target.value = ''; }}
                  />
                </div>
              )}

              {providerCfg?.supports_audio_ref && (
                <div className="mt-4">
                  <label className="flex items-center gap-1.5 text-white font-semibold mb-2 text-sm">
                    <Music2 className="w-4 h-4 text-purple-300" /> Audio{providerCfg.max_audios > 1 ? 's de référence' : ''} {providerCfg.requires_audio ? `(requis, max ${providerCfg.max_duration}s)` : `(optionnel, jusqu'à ${providerCfg.max_audios})`}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {refAudioFiles.map((f, idx) => (
                      <div key={idx} className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-gray-300">
                        <Music2 className="w-3.5 h-3.5 text-purple-300" /> {f.name.slice(0, 18)}
                        <button onClick={() => removeRefAudio(idx)}><X className="w-3 h-3 text-gray-400 hover:text-red-400" /></button>
                      </div>
                    ))}
                    {refAudioFiles.length < providerCfg.max_audios && (
                      <button
                        onClick={() => refAudioInputRef.current?.click()}
                        className="flex items-center gap-1.5 border-2 border-dashed border-white/20 rounded-lg px-3 py-1.5 text-xs text-gray-400 hover:text-purple-300 hover:border-purple-400/50 transition"
                      >
                        <Upload className="w-3.5 h-3.5" /> Ajouter
                      </button>
                    )}
                  </div>
                  <input
                    ref={refAudioInputRef} type="file" multiple className="hidden"
                    onChange={e => { addRefAudioFiles(e.target.files, providerCfg.max_audios); e.target.value = ''; }}
                  />
                </div>
              )}

              {providerCfg && (
                <div className={`mt-5 flex items-center justify-between rounded-xl px-4 py-3 border ${
                  normalPrice > credits ? 'bg-red-500/10 border-red-500/30' : 'bg-black/20 border-white/10'
                }`}>
                  <span className="text-gray-400 text-xs flex items-center gap-1.5">
                    <Coins className="w-3.5 h-3.5" /> Coût de cette génération
                  </span>
                  <span className={`font-bold text-sm ${normalPrice > credits ? 'text-red-300' : 'text-amber-300'}`}>
                    {normalPrice.toLocaleString('fr-FR')} Ar
                  </span>
                </div>
              )}
              {providerCfg?.bills_ref_seconds && normalRefSecs > 0 && (
                  <p className="text-[10px] text-gray-500 mt-1.5 leading-snug px-1">
                    Ce prix compte <b className="text-gray-300">{Math.round(normalRefSecs)} s de vidéo fournie</b> + {effectiveGenDuration(providerCfg, duration, normalRefSecs)} s générées (le moteur facture les deux).
                    {effectiveGenDuration(providerCfg, duration, normalRefSecs) < duration && ` La durée générée est limitée à ${effectiveGenDuration(providerCfg, duration, normalRefSecs)} s (vidéo fournie + générée ≤ ${providerCfg.ref_total_cap} s).`}
                  </p>
                )}

              <button
                onClick={generate}
                disabled={!effectivePrompt.trim() || generating}
                className="group w-full mt-3 py-3.5 rounded-xl font-bold text-white flex items-center justify-center gap-2 transition disabled:opacity-40 disabled:cursor-not-allowed hover:scale-[1.015] active:scale-[0.99] shadow-[0_8px_24px_rgba(147,51,234,0.35)]"
                style={{ background: 'linear-gradient(135deg, #9333EA, #DB2777, #9333EA)', backgroundSize: '200% 200%' }}
              >
                {generating ? <>Génération en cours...</> : <><Film className="w-5 h-5 group-hover:rotate-12 transition-transform" /> Générer la vidéo</>}
              </button>
            </div>
            </div>
          </div>
        )}

        {mode === 'storyboard' && (
          <div className="grid lg:grid-cols-[250px_1fr_340px] gap-5 items-start">
            {/* Colonne gauche : liste des storyboards, toujours visible */}
            <div className="lg:sticky lg:top-44">
              <div className="bg-[#140a24]/95 border border-purple-500/15 rounded-2xl p-4 shadow-[0_0_30px_rgba(147,51,234,0.08)]">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-white font-bold text-sm flex items-center gap-1.5">
                    <Clapperboard className="w-4 h-4 text-purple-300" /> Vos storyboards
                  </span>
                  <span className="text-[10px] text-gray-500 font-semibold">{storyboards.length}/5</span>
                </div>
                <button
                  onClick={createStoryboard}
                  disabled={storyboards.length >= 5}
                  className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-bold text-white mb-3 transition hover:scale-[1.02] disabled:opacity-40 disabled:hover:scale-100"
                  style={{ background: 'linear-gradient(135deg, #9333EA, #DB2777)' }}
                >
                  {storyboards.length >= 5 ? <Lock className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  {storyboards.length >= 5 ? 'Limite atteinte' : 'Nouveau storyboard'}
                </button>
                {storyboards.length >= 5 && (
                  <p className="text-[10px] text-gray-500 mb-3 leading-relaxed">Limite de 5 storyboards par compte pour l'instant. Une offre pour en avoir plus arrive bientôt.</p>
                )}
                {loadingStoryboards ? (
                  <div className="text-center text-gray-400 text-xs py-6">Chargement...</div>
                ) : storyboards.length === 0 ? (
                  <div className="text-center text-gray-500 text-xs py-6">Aucun storyboard pour l'instant.</div>
                ) : (
                  <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto pr-1">
                    {storyboards.map(sb => (
                      <div key={sb.storyboard_id} className="relative">
                        <button
                          onClick={() => openStoryboard(sb.storyboard_id)}
                          className={`w-full text-left pl-3 pr-10 py-2.5 rounded-xl border text-xs transition ${activeStoryboard?.storyboard_id === sb.storyboard_id ? 'bg-purple-600/20 border-purple-400 text-white shadow-[0_0_16px_rgba(147,51,234,0.25)]' : 'bg-white/5 border-white/10 text-gray-300 hover:border-white/25'}`}
                        >
                          <div className="font-semibold truncate">{sb.title}</div>
                          <div className="text-[10px] text-gray-500 mt-0.5 flex items-center gap-1">
                            <Layers className="w-3 h-3" /> {sb.scene_count} scène(s)
                            {sb.final_video_url && <Check className="w-3 h-3 text-green-400 ml-1" />}
                          </div>
                        </button>
                        <button
                          onClick={() => deleteStoryboard(sb.storyboard_id, sb.title)}
                          title="Supprimer"
                          className="absolute top-1/2 -translate-y-1/2 right-2 w-7 h-7 rounded-lg bg-black/30 hover:bg-red-500/60 flex items-center justify-center transition"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-gray-300" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {!activeStoryboard ? (
              <div className="lg:col-span-2 text-center text-gray-400 text-sm py-20 bg-[#140a24]/60 border border-purple-500/10 rounded-2xl flex flex-col items-center gap-3">
                <Clapperboard className="w-10 h-10 text-purple-500/40" />
                Créez ou sélectionnez un storyboard pour commencer.
              </div>
            ) : (
              <>
                {/* Colonne centrale : titre + scènes + composer */}
                <div className="flex flex-col gap-4 min-w-0">
                  {/* Titre renommable */}
                  <div className="flex items-center gap-2">
                    {editingTitle ? (
                      <>
                        <input
                          value={titleDraft} onChange={e => setTitleDraft(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') setEditingTitle(false); }}
                          autoFocus
                          className="flex-1 bg-black/30 border border-purple-400 rounded-xl px-3 py-2 text-white text-sm font-bold focus:outline-none"
                        />
                        <button onClick={saveTitle} disabled={!titleDraft.trim() || savingTitle} className="w-9 h-9 rounded-xl bg-purple-600 hover:bg-purple-500 disabled:opacity-40 flex items-center justify-center flex-shrink-0">
                          {savingTitle ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Check className="w-4 h-4 text-white" />}
                        </button>
                        <button onClick={() => setEditingTitle(false)} className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center flex-shrink-0">
                          <X className="w-4 h-4 text-white" />
                        </button>
                      </>
                    ) : (
                      <>
                        <h2 className="text-white font-black text-lg truncate">{activeStoryboard.title}</h2>
                        <button onClick={startEditTitle} className="w-7 h-7 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center flex-shrink-0 transition">
                          <Pencil className="w-3.5 h-3.5 text-gray-400" />
                        </button>
                      </>
                    )}
                  </div>

                  {/* Scènes, avec connecteurs "chaîne" entre elles */}
                  <div className="flex flex-col">
                    {scenes.map((scene, idx) => (
                      <React.Fragment key={scene.scene_id}>
                        {idx > 0 && (
                          <div className="flex items-center justify-center py-1">
                            <div className="w-0.5 h-6 bg-gradient-to-b from-purple-500/50 to-fuchsia-500/50" />
                          </div>
                        )}
                        <div className="relative bg-[#140a24]/95 border border-purple-500/15 rounded-2xl p-4 shadow-[0_4px_20px_rgba(0,0,0,0.3)]">
                          <div className="flex items-center gap-2.5 mb-2">
                            <div
                              className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-black flex-shrink-0"
                              style={{ background: 'linear-gradient(135deg, #9333EA, #DB2777)', boxShadow: '0 0 12px rgba(147,51,234,0.5)' }}
                            >
                              {idx + 1}
                            </div>
                            <span className="text-white font-bold text-xs flex-1">{providers[scene.provider]?.label || scene.provider}</span>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                              scene.status === 'completed' ? 'bg-green-500/20 text-green-300'
                              : scene.status === 'failed' ? 'bg-red-500/20 text-red-300'
                              : 'bg-amber-500/20 text-amber-300'
                            }`}>
                              {scene.status === 'completed' ? 'Terminée' : scene.status === 'failed' ? 'Échouée' : 'En cours...'}
                            </span>
                            {(scene.status === 'completed' || scene.status === 'failed') && (
                              <button
                                onClick={() => startEditScene(scene)}
                                title="Modifier et régénérer cette scène"
                                className="flex-shrink-0 w-6 h-6 rounded-lg bg-white/5 hover:bg-amber-500/20 border border-white/10 hover:border-amber-400/40 flex items-center justify-center transition"
                              >
                                <Pencil className="w-3 h-3 text-gray-400 hover:text-amber-300" />
                              </button>
                            )}
                          </div>
                          <p className="text-gray-400 text-xs mb-2 line-clamp-2">{scene.prompt}</p>
                          {scene.status === 'queued' && <TeckiALoader subtitle="Génération de la scène..." />}
                          {scene.status === 'failed' && <div className="text-red-300 text-xs py-2">{scene.error}</div>}
                          {scene.status === 'completed' && scene.video_url && (
                            <video src={scene.video_url} controls className="w-full rounded-xl" />
                          )}
                        </div>
                      </React.Fragment>
                    ))}
                    {scenes.length > 0 && (
                      <div className="flex items-center justify-center py-1">
                        <ChevronDown className="w-5 h-5 text-purple-400/60" />
                      </div>
                    )}
                  </div>

                  {/* Nouvelle scène - composer complet, reutilise pour "Modifier" une scene existante */}
                  <div
                    id="scene-composer"
                    className="relative bg-[#140a24]/95 rounded-2xl p-4 overflow-hidden"
                    style={{
                      border: editingSceneId ? '1.5px dashed rgba(251,191,36,0.5)' : '1.5px dashed rgba(196,132,252,0.4)',
                      boxShadow: editingSceneId ? '0 0 30px rgba(251,191,36,0.12)' : '0 0 30px rgba(147,51,234,0.12)',
                    }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-white font-bold text-sm flex items-center gap-1.5">
                        {editingSceneId ? (
                          <><Pencil className="w-4 h-4 text-amber-300" /> Modifier & régénérer la scène</>
                        ) : (
                          <><Sparkle className="w-4 h-4 text-fuchsia-300" /> Nouvelle scène</>
                        )}
                      </div>
                      {editingSceneId && (
                        <button onClick={cancelEditScene} className="text-[10px] text-gray-400 hover:text-white font-bold">
                          Annuler
                        </button>
                      )}
                    </div>
                    {editingSceneId && (
                      <p className="text-amber-200/80 text-[11px] mb-3">
                        Cette scène remplacera l'ancienne (même position). La nouvelle génération est facturée normalement, sans remboursement de l'ancienne.
                      </p>
                    )}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {providerTiles.map(({ key, cfg }) => {
                        const active = key === 'veo_group' ? isVeoTierKey(sceneProvider) : sceneProvider === key;
                        const label = key === 'veo_group' ? 'TeckiA V' : cfg.label;
                        const info = key === 'veo_group' ? VEO_GROUP_INFO : cfg.info;
                        const theme = providerTheme(key);
                        const Icon = theme.icon;
                        return (
                          <div key={key} className="relative group">
                            {key === 'seedance_mini' && (
                              <span className="absolute -top-2 right-1 z-10 px-1.5 py-0.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[8px] font-bold shadow-[0_0_8px_rgba(16,185,129,0.6)]">
                                ⭐ Meilleur choix
                              </span>
                            )}
                            <button
                              onClick={() => onSceneProviderTileClick(key)}
                              className={`w-full flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-[11px] font-bold border transition-all duration-300 ${
                                active
                                  ? `bg-gradient-to-br ${theme.color} border-white/40 text-white ${theme.glow}`
                                  : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/25'
                              }`}
                            >
                              <div className={`flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center bg-gradient-to-br ${theme.color}`}>
                                <Icon className="w-3 h-3 text-white" />
                              </div>
                              <span className="truncate">{label}</span>
                            </button>
                            {info && (
                              <div className="pointer-events-none absolute z-20 left-0 right-0 top-full mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-150">
                                <div className="bg-[#1c0f30] border border-purple-500/30 rounded-xl p-3 text-[11px] text-gray-300 leading-relaxed shadow-2xl">
                                  {info}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex gap-2 mb-3 bg-black/20 border border-white/10 rounded-xl p-1">
                      <button
                        onClick={() => setScenePromptMode('free')}
                        className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[11px] font-bold transition ${scenePromptMode === 'free' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-gray-200'}`}
                      >
                        <MessageSquare className="w-3 h-3" /> Prompt libre
                      </button>
                      <button
                        onClick={() => setScenePromptMode('structured')}
                        className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[11px] font-bold transition ${scenePromptMode === 'structured' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-gray-200'}`}
                      >
                        <SlidersHorizontal className="w-3 h-3" /> Champs détaillés
                      </button>
                    </div>

                    {scenePromptMode === 'free' ? (
                    <>
                    <div className="flex gap-2 mb-3">
                      <textarea
                        value={sceneBrief}
                        onChange={e => setSceneBrief(e.target.value)}
                        rows={2}
                        placeholder="Que voulez-vous pour cette scène ? (TeckiA rédige le prompt)"
                        className="flex-1 bg-black/30 border border-white/10 rounded-xl px-3 py-2.5 text-white text-xs placeholder-gray-500 focus:outline-none focus:border-purple-400 resize-y min-h-[76px]"
                      />
                      <button
                        onClick={generatePromptForScene}
                        disabled={!sceneBrief.trim() || generatingScenePrompt}
                        title="Générer le prompt"
                        className="flex-shrink-0 w-9 rounded-xl bg-purple-600 hover:bg-purple-500 disabled:opacity-40 flex items-center justify-center transition"
                      >
                        {generatingScenePrompt ? <Loader2 className="w-3.5 h-3.5 text-white animate-spin" /> : <MessageSquarePlus className="w-3.5 h-3.5 text-white" />}
                      </button>
                    </div>
                    <textarea
                      value={scenePrompt}
                      onChange={e => setScenePrompt(e.target.value)}
                      rows={2}
                      placeholder="Décrivez cette scène..."
                      className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2.5 text-white text-xs placeholder-gray-500 focus:outline-none focus:border-purple-400 resize-y min-h-[76px] mb-3"
                    />
                    <OmniDurationWarning provider={sceneProvider} text={scenePrompt} />
                    </>
                    ) : (
                    <div className="mb-3 space-y-2">
                      {STRUCTURED_FIELDS.map(f => {
                        const value = { environment: sceneFieldEnvironment, ambiance: sceneFieldAmbiance, character: sceneFieldCharacter, outfit: sceneFieldOutfit, movement: sceneFieldMovement, action: sceneFieldAction, info: sceneFieldInfo }[f.key];
                        const setter = sceneStructuredSetters[f.key];
                        return (
                          <div key={f.key}>
                            <label className="block text-gray-400 text-[10px] font-semibold mb-1">{f.label}</label>
                            <div className="flex items-center gap-1.5">
                              <input
                                value={value}
                                onChange={e => setter(e.target.value)}
                                onFocus={() => setSceneStructuredFocus(f.key)}
                                placeholder={f.placeholder}
                                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-xs placeholder-gray-500 focus:outline-none focus:border-purple-400"
                              />
                              <button
                                type="button"
                                onClick={() => openSceneFieldImagePicker(f.key)}
                                title={`Attacher une image à ${f.label}`}
                                disabled={sceneRefFiles.length >= (sceneCfg?.max_images || 7)}
                                className="flex-shrink-0 w-7 h-7 rounded-lg border border-white/10 bg-white/5 hover:bg-purple-600/30 hover:border-purple-400/50 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-gray-400 hover:text-purple-300 transition"
                              >
                                <Paperclip className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      <input
                        type="file" accept="image/*" multiple ref={sceneFieldFileInputRef} className="hidden"
                        onChange={e => {
                          if (pendingSceneFieldKey) attachImageToSceneField(pendingSceneFieldKey, e.target.files);
                          e.target.value = '';
                          setPendingSceneFieldKey(null);
                        }}
                      />
                    </div>
                    )}
                    {sceneCfg && isVeoTierKey(sceneProvider) && (
                      <div className="mb-3">
                        <div className="text-[10px] text-gray-400 mb-1">Qualité TeckiA V</div>
                        <div className="flex gap-1">
                          {VEO_TIERS.filter(t => providers[t.key]).map(t => (
                            <button
                              key={t.key}
                              onClick={() => onSceneTierChange(t.key)}
                              className={`flex-1 px-2 py-1.5 rounded text-[10px] font-bold border transition ${
                                sceneProvider === t.key
                                  ? 'bg-purple-600 border-purple-400 text-white'
                                  : 'bg-white/5 border-white/10 text-gray-400'
                              }`}
                            >
                              {t.short}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {sceneCfg && (
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <div className="text-[10px] text-gray-400 mb-1">Résolution</div>
                          <div className="flex flex-wrap gap-1">
                            {sceneCfg.resolutions.map(r => (
                              <button
                                key={r} onClick={() => {
                                  setSceneResolution(r);
                                  if (sceneCfg.video_ref_unsupported_resolutions?.includes(r)) {
                                    setSceneVideoFile(null); setSceneRefVideoFiles([]);
                                  }
                                }}
                                className={`px-2 py-1 rounded text-[10px] font-bold border transition ${sceneResolution === r ? 'bg-purple-600 border-purple-400 text-white' : 'bg-white/5 border-white/10 text-gray-400'}`}
                              >
                                {r}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] text-gray-400 mb-1">Durée : {sceneDuration}s</div>
                          {sceneProvider === 'omni' ? (
                            <p className="text-[10px] text-gray-500 mt-1">Durée fixe (même prix).</p>
                          ) : sceneCfg?.requires_audio ? (
                            <p className="text-[10px] text-gray-500 mt-1">Suit la durée de l'audio fourni.</p>
                          ) : sceneCfg?.requires_video ? (
                            <p className="text-[10px] text-gray-500 mt-1">Suit la durée de la vidéo fournie.</p>
                          ) : (
                            <input
                              type="range" min={1} max={sceneCfg.max_duration} value={sceneDuration}
                              onChange={e => setSceneDuration(parseInt(e.target.value, 10))}
                              className="w-full accent-purple-500"
                              disabled={!!sceneVideoFile}
                            />
                          )}
                        </div>
                      </div>
                    )}
                    {sceneCfg && sceneCfg.aspect_ratios.length > 1 && (
                      <div className="mb-3">
                        <div className="text-[10px] text-gray-400 mb-1">Format</div>
                        <div className="flex flex-wrap gap-1">
                          {sceneCfg.aspect_ratios.map(ar => (
                            <button
                              key={ar} onClick={() => setSceneAspectRatio(ar)}
                              className={`px-2 py-1 rounded text-[10px] font-bold border transition ${sceneAspectRatio === ar ? 'bg-purple-600 border-purple-400 text-white' : 'bg-white/5 border-white/10 text-gray-400'}`}
                            >
                              {ar}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {sceneCfg?.supports_generate_audio && (
                      <div className="mb-3 flex items-center justify-between bg-black/20 border border-white/10 rounded-xl px-3 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <Music2 className="w-3.5 h-3.5 text-purple-300" />
                          <span className="text-white text-xs font-semibold">Générer l'audio</span>
                        </div>
                        <button
                          onClick={() => setSceneGenerateAudio(v => !v)}
                          className={`w-10 h-5.5 rounded-full transition relative flex-shrink-0 ${sceneGenerateAudio ? 'bg-purple-600' : 'bg-white/10'}`}
                          style={{ width: 40, height: 22 }}
                        >
                          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${sceneGenerateAudio ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </button>
                      </div>
                    )}

                    {scenes.length > 0 && (
                      <label className="mb-3 flex items-center gap-2 bg-black/20 border border-white/10 rounded-lg px-3 py-2 cursor-pointer">
                        <input
                          type="checkbox" checked={sceneUseLastFrame}
                          onChange={e => setSceneUseLastFrame(e.target.checked)}
                          className="w-3.5 h-3.5 accent-purple-500"
                        />
                        <span className="text-[11px] text-gray-300">
                          Utiliser la dernière image de la scène précédente (continuité)
                        </span>
                      </label>
                    )}
                    {scenes.length > 0 && lastSceneQueued && (
                      <p className={`mb-3 text-[10px] ${sceneUseLastFrame ? 'text-amber-300' : 'text-emerald-300'}`}>
                        {sceneUseLastFrame
                          ? "⏳ La scène précédente est en cours : attendez qu'elle finisse, ou décochez la case ci-dessus pour lancer celle-ci en parallèle."
                          : "✓ Case décochée : vous pouvez lancer cette scène tout de suite, sans attendre la précédente."}
                      </p>
                    )}

                    <div className="mb-3">
                      <div className="text-[10px] text-gray-400 mb-1.5">
                        Photos de référence {sceneCfg?.requires_image && scenes.length === 0 ? '(requis pour la 1ère scène)' : `(optionnel, jusqu'à ${sceneCfg?.max_images || 7})`}
                      </div>
                      {scenes.length > 0 && sceneUseLastFrame && (
                        <p className="text-[10px] text-purple-300/80 mb-1.5">
                          {sceneRefFiles.length === 0
                            ? '↳ Continuité pure depuis la scène précédente.'
                            : '↳ Continuité + vos photos de référence ci-dessous seront combinées.'}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {sceneRefPreviews.map((src, idx) => (
                          <button
                            key={idx} type="button" onClick={() => insertSceneImageTag(idx + 1)}
                            className="relative w-14 h-14 rounded-lg overflow-hidden border border-purple-500/25 hover:border-purple-400 transition"
                          >
                            <img src={src} alt="" className="w-full h-full object-cover" />
                            <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-center py-0.5">
                              <span className="text-[7px] font-bold text-purple-300">@image{idx + 1}</span>
                            </div>
                            <div
                              role="button"
                              onClick={e => { e.stopPropagation(); removeSceneRef(idx); }}
                              className="absolute top-0.5 right-0.5 w-4 h-4 bg-black/70 hover:bg-red-600 rounded-full flex items-center justify-center"
                            >
                              <X className="w-2.5 h-2.5 text-white" />
                            </div>
                          </button>
                        ))}
                        {sceneRefFiles.length < (sceneCfg?.max_images || 7) && (
                          <button
                            onClick={() => sceneFileInputRef.current?.click()}
                            className="w-14 h-14 rounded-lg border-2 border-dashed border-white/20 flex items-center justify-center text-gray-400 hover:text-purple-300 hover:border-purple-400/50 transition"
                          >
                            <Upload className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <input
                          ref={sceneFileInputRef} type="file" accept="image/*" multiple className="hidden"
                          onChange={e => { addSceneRefFiles(e.target.files, sceneCfg?.max_images || 7); e.target.value = ''; }}
                        />
                      </div>
                    </div>

                    {sceneCfg?.supports_video_ref && sceneCfg.max_videos === 1
                      && !sceneCfg.video_ref_unsupported_resolutions?.includes(sceneResolution) && (
                      <div className="mb-3">
                        <div className="text-[10px] text-gray-400 mb-1.5 flex items-center gap-1"><Video className="w-3 h-3 text-purple-300" /> Vidéo de référence {sceneCfg.requires_video ? `(requis, max ${sceneCfg.max_duration}s)` : '(optionnel)'}</div>
                        {sceneVideoPreview ? (
                          <div className="relative inline-block">
                            <video src={sceneVideoPreview} className="w-32 h-20 rounded-lg object-contain bg-black border border-purple-500/25" muted />
                            <button onClick={removeSceneVideo} className="absolute top-1 right-1 w-5 h-5 bg-black/70 hover:bg-red-600 rounded-full flex items-center justify-center">
                              <X className="w-3 h-3 text-white" />
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => sceneVideoInputRef.current?.click()} className="w-32 h-20 rounded-lg border-2 border-dashed border-white/20 flex flex-col items-center justify-center text-gray-400 hover:text-purple-300 hover:border-purple-400/50 transition">
                            <Video className="w-4 h-4 mb-1" /><span className="text-[9px]">Ajouter</span>
                          </button>
                        )}
                        <input ref={sceneVideoInputRef} type="file" accept="video/*" className="hidden"
                          onChange={e => { onSceneVideoPicked(e.target.files?.[0]); e.target.value = ''; }} />
                      </div>
                    )}

                    {sceneCfg?.supports_first_last_frame && (
                      <div className="mb-3">
                        <div className="text-[10px] text-gray-400 mb-1.5 flex items-center gap-1"><ImagePlus className="w-3 h-3 text-purple-300" /> Première / dernière image</div>
                        <div className="flex gap-3">
                          {[{ key: 'first', label: 'Première', preview: sceneFirstFramePreview, ref: sceneFirstFrameInputRef },
                            { key: 'last', label: 'Dernière', preview: sceneLastFramePreview, ref: sceneLastFrameInputRef }].map(f => (
                            <div key={f.key}>
                              {f.preview ? (
                                <div className="relative w-14 h-14 rounded-lg overflow-hidden border border-purple-500/25">
                                  <img src={f.preview} alt="" className="w-full h-full object-cover" />
                                  <button onClick={() => removeSceneFrame(f.key)} className="absolute top-0.5 right-0.5 w-4 h-4 bg-black/70 hover:bg-red-600 rounded-full flex items-center justify-center">
                                    <X className="w-2.5 h-2.5 text-white" />
                                  </button>
                                </div>
                              ) : (
                                <button onClick={() => f.ref.current?.click()} className="w-14 h-14 rounded-lg border-2 border-dashed border-white/20 flex flex-col items-center justify-center text-gray-400 hover:text-purple-300 hover:border-purple-400/50 transition">
                                  <Upload className="w-3.5 h-3.5" /><span className="text-[8px] mt-0.5">{f.label}</span>
                                </button>
                              )}
                              <input ref={f.ref} type="file" accept="image/*" className="hidden"
                                onChange={e => { onSceneFramePicked(f.key, e.target.files?.[0]); e.target.value = ''; }} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {sceneCfg?.supports_video_ref && sceneCfg.max_videos > 1 && (
                      <div className="mb-3">
                        <div className="text-[10px] text-gray-400 mb-1.5 flex items-center gap-1"><Video className="w-3 h-3 text-purple-300" /> Vidéos de référence (jusqu'à {sceneCfg.max_videos})</div>
                        <div className="flex flex-wrap gap-1.5">
                          {sceneRefVideoFiles.map((f, idx) => (
                            <div key={idx} className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-[10px] text-gray-300">
                              <Video className="w-3 h-3 text-purple-300" /> {f.name.slice(0, 14)}
                              <button onClick={() => removeSceneRefVideo(idx)}><X className="w-2.5 h-2.5 text-gray-400 hover:text-red-400" /></button>
                            </div>
                          ))}
                          {sceneRefVideoFiles.length < sceneCfg.max_videos && (
                            <button onClick={() => sceneRefVideoInputRef.current?.click()} className="flex items-center gap-1 border-2 border-dashed border-white/20 rounded-lg px-2.5 py-1 text-[10px] text-gray-400 hover:text-purple-300 hover:border-purple-400/50 transition">
                              <Upload className="w-3 h-3" /> Ajouter
                            </button>
                          )}
                        </div>
                        <input ref={sceneRefVideoInputRef} type="file" accept="video/*" multiple className="hidden"
                          onChange={e => { addSceneRefVideoFiles(e.target.files, sceneCfg.max_videos); e.target.value = ''; }} />
                      </div>
                    )}

                    {sceneCfg?.supports_audio_ref && (
                      <div className="mb-3">
                        <div className="text-[10px] text-gray-400 mb-1.5 flex items-center gap-1"><Music2 className="w-3 h-3 text-purple-300" /> Audio{sceneCfg.max_audios > 1 ? 's de référence' : ''} {sceneCfg.requires_audio ? `(requis, max ${sceneCfg.max_duration}s)` : `(jusqu'à ${sceneCfg.max_audios})`}</div>
                        <div className="flex flex-wrap gap-1.5">
                          {sceneRefAudioFiles.map((f, idx) => (
                            <div key={idx} className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-[10px] text-gray-300">
                              <Music2 className="w-3 h-3 text-purple-300" /> {f.name.slice(0, 14)}
                              <button onClick={() => removeSceneRefAudio(idx)}><X className="w-2.5 h-2.5 text-gray-400 hover:text-red-400" /></button>
                            </div>
                          ))}
                          {sceneRefAudioFiles.length < sceneCfg.max_audios && (
                            <button onClick={() => sceneRefAudioInputRef.current?.click()} className="flex items-center gap-1 border-2 border-dashed border-white/20 rounded-lg px-2.5 py-1 text-[10px] text-gray-400 hover:text-purple-300 hover:border-purple-400/50 transition">
                              <Upload className="w-3 h-3" /> Ajouter
                            </button>
                          )}
                        </div>
                        <input ref={sceneRefAudioInputRef} type="file" multiple className="hidden"
                          onChange={e => { addSceneRefAudioFiles(e.target.files, sceneCfg.max_audios); e.target.value = ''; }} />
                      </div>
                    )}

                    {sceneCfg && (
                      <div className={`mb-3 flex items-center justify-between rounded-xl px-3 py-2.5 border ${
                        scenePrice > credits ? 'bg-red-500/10 border-red-500/30' : 'bg-black/20 border-white/10'
                      }`}>
                        <span className="text-gray-400 text-[11px] flex items-center gap-1.5">
                          <Coins className="w-3.5 h-3.5" /> Coût de cette scène
                        </span>
                        <span className={`font-bold text-xs ${scenePrice > credits ? 'text-red-300' : 'text-amber-300'}`}>
                          {scenePrice.toLocaleString('fr-FR')} Ar
                        </span>
                      </div>
                    )}
                    {sceneCfg?.bills_ref_seconds && sceneRefSecs > 0 && (
                  <p className="text-[10px] text-gray-500 mt-1.5 leading-snug px-1">
                    Ce prix compte <b className="text-gray-300">{Math.round(sceneRefSecs)} s de vidéo fournie</b> + {effectiveGenDuration(sceneCfg, sceneDuration, sceneRefSecs)} s générées (le moteur facture les deux).
                    {effectiveGenDuration(sceneCfg, sceneDuration, sceneRefSecs) < sceneDuration && ` La durée générée est limitée à ${effectiveGenDuration(sceneCfg, sceneDuration, sceneRefSecs)} s (vidéo fournie + générée ≤ ${sceneCfg.ref_total_cap} s).`}
                  </p>
                )}

                    <button
                      onClick={addScene}
                      disabled={!sceneEffectivePrompt.trim() || addingScene || (!editingSceneId && sceneUseLastFrame && lastSceneQueued)}
                      className="w-full py-2.5 rounded-xl font-bold text-white text-sm flex items-center justify-center gap-2 disabled:opacity-40 transition hover:scale-[1.01]"
                      style={{ background: editingSceneId ? 'linear-gradient(135deg, #D97706, #DB2777)' : 'linear-gradient(135deg, #9333EA, #DB2777)' }}
                    >
                      {addingScene ? <Loader2 className="w-4 h-4 animate-spin" /> : editingSceneId ? <RefreshCw className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                      {editingSceneId ? 'Régénérer cette scène' : 'Ajouter la scène'}
                    </button>
                  </div>

                  <Retention14DaysNotice />
                </div>

                {/* Colonne droite : résultat assemblé, toujours visible */}
                <div className="lg:sticky lg:top-44">
                  <div className="relative bg-[#140a24]/95 border border-purple-500/25 rounded-2xl p-4 shadow-[0_0_40px_rgba(147,51,234,0.15)] overflow-hidden">
                    <div className="text-white font-bold text-sm mb-3 flex items-center gap-1.5">
                      <Clapperboard className="w-4 h-4 text-purple-300" /> Film assemblé
                    </div>
                    {activeStoryboard.final_video_url ? (
                      <>
                        <video src={activeStoryboard.final_video_url} controls className="w-full rounded-xl mb-3 shadow-[0_8px_24px_rgba(0,0,0,0.4)]" />
                        <a
                          href={activeStoryboard.final_video_url} download target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition w-full justify-center"
                        >
                          <Download className="w-3.5 h-3.5" /> Télécharger le film
                        </a>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-10 text-center gap-2">
                        <Clapperboard className="w-9 h-9 text-purple-500/30" />
                        <p className="text-gray-500 text-xs">Pas encore assemblé.<br />Ajoutez au moins 2 scènes terminées.</p>
                      </div>
                    )}
                    {completedScenesCount >= 2 && (
                      <button
                        onClick={assembleFilm}
                        disabled={assembling}
                        className="w-full mt-3 py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2 disabled:opacity-40 transition hover:scale-[1.01]"
                        style={{ background: 'linear-gradient(135deg, #059669, #10B981)' }}
                      >
                        {assembling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                        {assembling ? 'Assemblage en cours...' : activeStoryboard.final_video_url ? 'Réassembler (mettre à jour)' : 'Assembler le film'}
                      </button>
                    )}
                    <p className="text-[10px] text-gray-500 mt-2 text-center">Utilise toutes les {completedScenesCount} scène(s) terminée(s) depuis le début.</p>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {mode === 'film' && (
          <div className={`${(filmStage === 'script' || filmStage === 'production') ? 'max-w-5xl' : 'max-w-3xl'} mx-auto`}>
            {filmStage === 'brief' && (
              <div className="relative bg-[#140a24]/95 border border-purple-500/15 rounded-2xl p-6 shadow-[0_0_40px_rgba(147,51,234,0.08)]">
                <div className="flex items-center gap-2 mb-1.5">
                  <Sparkles className="w-5 h-5 text-purple-300" />
                  <h2 className="text-white font-black text-lg">Le Réalisateur IA</h2>
                </div>
                <p className="text-gray-400 text-xs mb-5 leading-relaxed">
                  TeckiA devient votre <b className="text-purple-200">réalisateur, scénariste et concepteur</b>. Racontez votre idée avec tout ce qui compte pour vous
                  (personnages, lieu, ambiance, fin souhaitée, style visuel…) : TeckiA écrit l'histoire complète, puis vous la montre
                  <b className="text-purple-200"> avant toute production</b>. Rien n'est facturé tant que vous n'avez pas validé.
                </p>

                <div className="grid sm:grid-cols-2 gap-2.5 mb-5">
                  {[{ v: 'ai', t: '✨ TeckiA invente mon film', d: "Vous donnez une idée ou un résumé : TeckiA écrit toutes les scènes, vous vérifiez chacune." },
                    { v: 'import', t: "📥 J'ai déjà mon film", d: 'Vous collez un film déjà préparé : TeckiA le découpe, le réalise et l’assemble sans rien changer.' }].map(o => (
                    <button
                      key={o.v}
                      onClick={() => { setFilmSource(o.v); setFilmScriptError(null); }}
                      className={`text-left p-3.5 rounded-xl border transition ${filmSource === o.v ? 'bg-purple-600/25 border-purple-400 shadow-[0_0_16px_rgba(147,51,234,0.3)]' : 'bg-white/5 border-white/10 hover:border-white/25'}`}
                    >
                      <div className="text-white text-sm font-bold">{o.t}</div>
                      <div className="text-gray-400 text-[11px] leading-snug mt-1">{o.d}</div>
                    </button>
                  ))}
                </div>

                {filmSource === 'import' && (
                  <div>
                    <label className="block text-white font-semibold mb-2 text-sm">Collez votre film</label>
                    <textarea
                      value={filmImportText}
                      onChange={e => setFilmImportText(e.target.value)}
                      rows={12}
                      placeholder={"Collez ici votre film déjà préparé : titre, style, personnages, puis les scènes (un paragraphe par scène, avec les dialogues entre guillemets et la durée si vous la connaissez). Indiquez « suite de la scène précédente » quand une scène continue exactement la précédente."}
                      className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-400 focus:shadow-[0_0_0_3px_rgba(147,51,234,0.15)] resize-y min-h-[160px] transition"
                    />
                    <p className="text-[10px] text-gray-500 mt-1.5 leading-relaxed">
                      TeckiA repère les scènes et les personnages mais <b className="text-gray-300">ne réécrit rien</b> : le texte de chaque scène est envoyé tel quel au moteur vidéo (TeckiA ajoute seulement les images des personnages et la gestion des voix).
                      Toutes les scènes sont validées d’avance : vous n’avez plus qu’à lancer. Jusqu’à 30 scènes.
                    </p>
                    <details className="mt-2 rounded-lg border border-white/10 bg-black/20">
                      <summary className="cursor-pointer select-none px-3 py-1.5 text-[11px] font-semibold text-purple-200">💡 Modèle de demande à donner à votre assistant IA pour préparer le film</summary>
                      <div className="px-3 pb-3">
                        <p className="text-[10px] text-gray-400 leading-relaxed whitespace-pre-line">{FILM_IMPORT_TEMPLATE}</p>
                        <button onClick={() => { try { navigator.clipboard.writeText(FILM_IMPORT_TEMPLATE); setFilmNotice('Modèle copié.'); } catch (e) { /* silencieux */ } }} className="mt-2 px-3 py-1.5 rounded-lg text-[10px] font-bold bg-white/5 hover:bg-white/10 border border-white/10 text-gray-200">Copier le modèle</button>
                      </div>
                    </details>
                  </div>
                )}

                {filmSource === 'ai' && (<>
                <label className="block text-white font-semibold mb-2 text-sm">Votre envie de film</label>
                <textarea
                  value={filmBrief}
                  onChange={e => setFilmBrief(e.target.value)}
                  rows={6}
                  placeholder="Ex : Un film d'aventure à Madagascar. Deux amis, Rado et Tiana, découvrent une carte cachée dans le vieux marché d'Antananarivo. Ambiance mystérieuse, lumière dorée, style cinéma réaliste. Ils traversent la forêt, un secret de famille se révèle, fin émouvante."
                  className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-400 focus:shadow-[0_0_0_3px_rgba(147,51,234,0.15)] resize-y min-h-[76px] transition"
                />
                </>)}

                <div className="mt-5">
                  <label className="block text-white font-semibold mb-2 text-sm">Langue des dialogues</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[{ v: 'fr', l: 'Français', s: 'TeckiA G' }, { v: 'en', l: 'English', s: 'TeckiA SM' }, { v: 'mg', l: 'Malagasy', s: 'TeckiA O' }].map(lg => (
                      <button
                        key={lg.v}
                        onClick={() => setFilmLanguage(lg.v)}
                        className={`py-2.5 rounded-xl text-xs font-bold border transition ${filmLanguage === lg.v ? 'bg-purple-600 border-purple-400 text-white shadow-[0_0_14px_rgba(147,51,234,0.4)]' : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/25'}`}
                      >
                        {lg.l}
                        <span className="block text-[9px] font-normal opacity-70 mt-0.5">{lg.s}</span>
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1.5">Les scènes sans dialogue utilisent TeckiA SM.</p>
                </div>

                <div className="grid sm:grid-cols-2 gap-5 mt-5">
                  <div className={filmSource === 'import' ? 'hidden' : ''}>
                    <label className="block text-white font-semibold mb-2 text-sm">
                      Durée du film <span className="text-purple-300 font-bold">({filmDuration}s)</span>
                    </label>
                    <input
                      type="range" min={30} max={120} step={5} value={filmDuration}
                      onChange={e => setFilmDuration(Number(e.target.value))}
                      className="w-full accent-purple-500"
                    />
                    <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                      <span>30s</span><span>2 min max</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-white font-semibold mb-2 text-sm">Format</label>
                    <div className="flex gap-2">
                      {[{ v: '16:9', l: 'Paysage' }, { v: '9:16', l: 'Story' }].map(ar => (
                        <button
                          key={ar.v}
                          onClick={() => setFilmAspectRatio(ar.v)}
                          className={`flex-1 py-2.5 rounded-xl text-xs font-bold border transition ${filmAspectRatio === ar.v ? 'bg-purple-600 border-purple-400 text-white' : 'bg-white/5 border-white/10 text-gray-400'}`}
                        >
                          {ar.l} <span className="opacity-60">{ar.v}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {filmScriptError && (
                  <div className="mt-4 text-red-300 text-xs bg-red-500/10 border border-red-500/20 rounded-xl px-3.5 py-2.5">{filmScriptError}</div>
                )}

                {filmSource === 'import' ? (
                  <button
                    onClick={importFilm}
                    disabled={filmImportText.trim().length < 80 || filmImporting}
                    className="group w-full mt-5 py-3.5 rounded-xl font-bold text-white flex items-center justify-center gap-2 disabled:opacity-40 transition hover:scale-[1.01]"
                    style={{ background: 'linear-gradient(135deg, #9333EA, #DB2777, #9333EA)', backgroundSize: '200% 200%' }}
                  >
                    {filmImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clapperboard className="w-4 h-4" />}
                    {filmImporting ? 'TeckiA découpe votre film...' : 'Analyser mon film'}
                  </button>
                ) : (
                <button
                  onClick={() => generateFilmScript([])}
                  disabled={!filmBrief.trim() || filmLoadingQuestions || filmGeneratingScript}
                  className="group w-full mt-5 py-3.5 rounded-xl font-bold text-white flex items-center justify-center gap-2 disabled:opacity-40 transition hover:scale-[1.01]"
                  style={{ background: 'linear-gradient(135deg, #9333EA, #DB2777, #9333EA)', backgroundSize: '200% 200%' }}
                >
                  {(filmLoadingQuestions || filmGeneratingScript) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 group-hover:rotate-12 transition-transform" />}
                  {filmGeneratingScript ? "TeckiA écrit votre histoire..." : "Créer l'histoire"}
                </button>
                )}
              </div>
            )}

            {filmStage === 'brief' && storyboards.length > 0 && (
              <div className="mt-6">
                <h3 className="text-white font-bold text-sm mb-2.5 flex items-center gap-1.5">
                  <Clapperboard className="w-4 h-4 text-purple-300" /> Vos films précédents
                </h3>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {storyboards.map(sb => (
                    <div key={sb.storyboard_id} className="relative">
                      <button
                        onClick={() => resumeFilm(sb.storyboard_id)}
                        className="w-full text-left pl-3.5 pr-11 py-3 rounded-xl border bg-white/5 border-white/10 hover:border-purple-400/50 transition flex items-center justify-between gap-2"
                      >
                        <div className="min-w-0">
                          <div className="text-white text-xs font-bold truncate">{sb.title}</div>
                          <div className="text-gray-500 text-[10px] mt-0.5">{sb.scene_count} scène(s)</div>
                        </div>
                        <span className={`flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          sb.status === 'assembled' ? 'bg-emerald-500/20 text-emerald-300'
                          : sb.status === 'in_progress' ? 'bg-amber-500/20 text-amber-300'
                          : 'bg-white/10 text-gray-400'
                        }`}>
                          {sb.status === 'assembled' ? 'Terminé' : sb.status === 'in_progress' ? 'En cours' : 'Brouillon'}
                        </span>
                      </button>
                      <button
                        onClick={() => deleteStoryboard(sb.storyboard_id, sb.title)}
                        title="Supprimer ce film"
                        className="absolute top-1/2 -translate-y-1/2 right-2 w-7 h-7 rounded-lg bg-black/30 hover:bg-red-500/60 flex items-center justify-center transition"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-gray-300" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {filmStage === 'questions' && (
              <div className="relative bg-[#140a24]/95 border border-purple-500/15 rounded-2xl p-6 shadow-[0_0_40px_rgba(147,51,234,0.08)]">
                <div className="flex items-center gap-2 mb-1.5">
                  <Users className="w-5 h-5 text-purple-300" />
                  <h2 className="text-white font-black text-lg">Quelques précisions</h2>
                </div>
                <p className="text-gray-400 text-xs mb-5 leading-relaxed">
                  Comme un vrai scénariste avant de se lancer, TeckiA a besoin de ces précisions pour écrire
                  une histoire vraiment adaptée. Répondez à ce qui vous parle, laissez le reste vide.
                </p>
                <div className="space-y-4">
                  {filmAnswers.map((qa, i) => (
                    <div key={i}>
                      <label className="block text-white text-sm font-semibold mb-1.5">{qa.question}</label>
                      <input
                        value={qa.answer}
                        onChange={e => setFilmAnswers(prev => prev.map((a, idx) => idx === i ? { ...a, answer: e.target.value } : a))}
                        placeholder="Votre réponse (optionnel)..."
                        className="w-full bg-black/30 border border-white/10 rounded-xl px-3.5 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-400 transition"
                      />
                    </div>
                  ))}
                </div>

                {filmScriptError && (
                  <div className="mt-4 text-red-300 text-xs bg-red-500/10 border border-red-500/20 rounded-xl px-3.5 py-2.5">{filmScriptError}</div>
                )}

                <div className="flex gap-3 mt-5">
                  <button
                    onClick={() => setFilmStage('brief')}
                    className="flex-1 py-3 rounded-xl font-bold text-gray-300 bg-white/5 hover:bg-white/10 border border-white/10 transition"
                  >
                    Retour
                  </button>
                  <button
                    onClick={() => generateFilmScript()}
                    disabled={filmGeneratingScript}
                    className="group flex-[2] py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2 disabled:opacity-40 transition hover:scale-[1.01]"
                    style={{ background: 'linear-gradient(135deg, #9333EA, #DB2777, #9333EA)' }}
                  >
                    {filmGeneratingScript ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 group-hover:rotate-12 transition-transform" />}
                    {filmGeneratingScript ? "L'IA écrit votre film..." : 'Écrire le scénario'}
                  </button>
                </div>
              </div>
            )}

            {filmStage === 'story' && filmScript && (
              <div className="space-y-5">
                <div className="relative overflow-hidden rounded-2xl border border-purple-500/25 p-6 shadow-[0_0_50px_rgba(147,51,234,0.12)]" style={{ background: 'linear-gradient(155deg, rgba(147,51,234,0.18), rgba(20,10,36,0.96) 55%, rgba(219,39,119,0.10))' }}>
                  <div className="flex items-center gap-2 text-[10px] font-bold tracking-widest text-purple-300 uppercase mb-2"><Sparkles className="w-3.5 h-3.5" /> Étape 1 · L'histoire</div>
                  <h2 className="text-white font-black text-2xl leading-tight">{filmScript.title}</h2>
                  <div className="flex flex-wrap gap-2 mt-3 text-[10px] font-semibold">
                    <span className="px-2.5 py-1 rounded-full bg-white/10 text-gray-200">🎬 {filmScript.scenes?.length || 0} scènes</span>
                    <span className="px-2.5 py-1 rounded-full bg-white/10 text-gray-200">⏱ ~{(filmScript.scenes || []).reduce((t, x) => t + (x.duration || 0), 0)} s</span>
                    <span className="px-2.5 py-1 rounded-full bg-white/10 text-gray-200">👥 {filmScript.characters?.length || 0} personnage{(filmScript.characters?.length || 0) > 1 ? 's' : ''}</span>
                    <span className="px-2.5 py-1 rounded-full bg-white/10 text-gray-200">{filmAspectRatio === '9:16' ? '📱 Story 9:16' : '🖥 Paysage 16:9'}</span>
                  </div>
                </div>

                <div className="bg-[#140a24]/95 border border-white/10 rounded-2xl p-5">
                  <h3 className="text-white font-bold text-sm mb-2 flex items-center gap-1.5">📝 Résumé</h3>
                  <p className="text-gray-200 text-sm leading-relaxed">{filmSummaryLang === 'mg' && filmSummaryMg ? filmSummaryMg.summary : filmScript.summary}</p>
                </div>

                {(filmScript.director_vision || filmScript.logline) && (
                  <div className="rounded-2xl border border-amber-400/25 bg-amber-500/5 p-5">
                    <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                      <h3 className="text-amber-200 font-bold text-sm flex items-center gap-1.5">🎬 Vision du réalisateur</h3>
                      <div className="flex gap-1.5">
                        {[['fr', '🇫🇷 Français'], ['mg', '🇲🇬 Malagasy']].map(([k, l]) => (
                          <button key={k} onClick={() => chooseSummaryLang(k)} disabled={filmAiBusy === 'sum'} className={`px-2.5 py-1 rounded-full text-[10px] font-bold border transition ${filmSummaryLang === k ? 'bg-amber-400 text-[#1b1204] border-amber-300' : 'bg-white/5 border-white/10 text-gray-300'}`}>{filmAiBusy === 'sum' && k === 'mg' ? '…' : l}</button>))}
                      </div>
                    </div>
                    {filmScript.logline && <p className="text-white text-sm font-semibold leading-snug mb-2">« {filmSummaryLang === 'mg' && filmSummaryMg ? filmSummaryMg.logline : filmScript.logline} »</p>}
                    {filmScript.director_vision && <p className="text-gray-200 text-[13px] leading-relaxed">{filmSummaryLang === 'mg' && filmSummaryMg ? filmSummaryMg.vision : filmScript.director_vision}</p>}
                    <div className="flex flex-wrap gap-1.5 mt-3 text-[10px] font-semibold">
                      {filmScript.tone && <span className="px-2.5 py-1 rounded-full bg-white/10 text-gray-200">🎭 Ton : {filmScript.tone}</span>}
                      {filmScript.motif && <span className="px-2.5 py-1 rounded-full bg-white/10 text-gray-200">🔁 Motif visuel : {filmScript.motif}</span>}
                    </div>
                  </div>
                )}

                <div className="bg-[#140a24]/95 border border-white/10 rounded-2xl p-5">
                  <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-1.5">📖 L'histoire complète</h3>
                  <div className="space-y-3">
                    {String(filmScript.story || filmScript.summary || '').split(/\n+/).filter(Boolean).map((p, i) => (
                      /^acte\s*\d/i.test(p.trim())
                        ? <h4 key={i} className="text-purple-300 font-extrabold text-sm pt-2 tracking-wide">{p.trim()}</h4>
                        : <p key={i} className="text-gray-300 text-sm leading-relaxed">{p}</p>
                    ))}
                  </div>
                </div>

                {filmScript.characters?.length > 0 && (
                  <div>
                    <h3 className="text-white font-bold text-sm mb-2.5 flex items-center gap-1.5"><Users className="w-4 h-4 text-purple-300" /> Les personnages</h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {filmScript.characters.map((c, i) => (
                        <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-3.5">
                          <div className="text-white text-xs font-bold mb-1">{c.name}</div>
                          <p className="text-gray-400 text-[11px] leading-relaxed">{c.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {filmScript.visual_style && (
                  <div className="bg-white/5 border border-white/10 rounded-xl p-3.5">
                    <div className="text-[10px] font-bold text-purple-300 uppercase tracking-wider mb-1">🎨 Style visuel du film</div>
                    <p className="text-gray-300 text-[11px] leading-relaxed">{filmScript.visual_style}</p>
                  </div>
                )}

                {filmScriptError && <div className="text-red-300 text-xs bg-red-500/10 border border-red-500/20 rounded-xl px-3.5 py-2.5">{filmScriptError}</div>}

                {filmShowExtra && (
                  <div className="bg-purple-500/10 border border-purple-400/30 rounded-2xl p-4">
                    <label className="block text-white font-semibold text-sm mb-1.5">Qu'aimeriez-vous changer ou ajouter ?</label>
                    <textarea
                      value={filmExtraInfo} onChange={e => setFilmExtraInfo(e.target.value)} rows={4} autoFocus
                      placeholder="Ex : Rado doit avoir une cicatrice. Ajoute une scène de tempête. La fin doit être plus joyeuse. Le méchant est le maire du village..."
                      className="w-full bg-black/30 border border-white/10 rounded-xl px-3.5 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-400 resize-y"
                    />
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => { setFilmShowExtra(false); setFilmExtraInfo(''); }} className="px-4 py-2.5 rounded-xl text-xs font-bold text-gray-300 bg-white/5 hover:bg-white/10 border border-white/10">Annuler</button>
                      <button
                        onClick={() => generateFilmScript(undefined, filmExtraInfo.trim())}
                        disabled={!filmExtraInfo.trim() || filmGeneratingScript}
                        className="flex-1 py-2.5 rounded-xl font-bold text-white text-sm flex items-center justify-center gap-2 disabled:opacity-40"
                        style={{ background: 'linear-gradient(135deg, #9333EA, #DB2777)' }}
                      >
                        {filmGeneratingScript ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        {filmGeneratingScript ? "TeckiA réécrit l'histoire..." : "Réécrire l'histoire avec mes infos"}
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3">
                  <button onClick={resetFilmFlow} className="sm:flex-1 py-3 rounded-xl font-bold text-gray-300 bg-white/5 hover:bg-white/10 border border-white/10 transition">Recommencer</button>
                  {!filmShowExtra && (
                    <button onClick={() => setFilmShowExtra(true)} className="sm:flex-[1.3] py-3 rounded-xl font-bold text-purple-100 bg-purple-600/25 hover:bg-purple-600/40 border border-purple-400/40 transition flex items-center justify-center gap-2">
                      ✍️ Ajouter des infos / modifier l'histoire
                    </button>
                  )}
                  <button
                    onClick={() => setFilmStage('script')}
                    disabled={filmGeneratingScript}
                    className="sm:flex-[1.6] py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2 disabled:opacity-40 transition hover:scale-[1.01]"
                    style={{ background: 'linear-gradient(135deg, #9333EA, #DB2777, #9333EA)' }}
                  >
                    <Check className="w-4 h-4" /> Valider l'histoire
                  </button>
                </div>
                <p className="text-[10px] text-gray-500 text-center">Valider ne coûte rien : vous verrez ensuite le plan de production complet (acteurs, scènes, modèles et prix) avant de lancer.</p>
              </div>
            )}

            {filmStage === 'script' && filmScript && (
              <div className="space-y-5">
                <div className="relative overflow-hidden rounded-2xl border border-purple-500/25 p-6 shadow-[0_0_50px_rgba(147,51,234,0.12)]" style={{ background: 'linear-gradient(155deg, rgba(147,51,234,0.18), rgba(20,10,36,0.96) 55%, rgba(219,39,119,0.10))' }}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 text-[10px] font-bold tracking-widest text-purple-300 uppercase"><Clapperboard className="w-3.5 h-3.5" /> Étape 2 · Plan de production</div>
                    <button onClick={() => setFilmStage('story')} className="text-[11px] font-semibold text-purple-200 hover:text-white underline underline-offset-2">← Revoir l'histoire</button>
                  </div>
                  <input
                    value={filmScript.title || ''}
                    onChange={e => updateFilmField('title', e.target.value)}
                    className="w-full bg-transparent border-b border-white/10 focus:border-purple-400 focus:outline-none text-white font-black text-xl mb-3 pb-1"
                  />
                  <p className="text-gray-300 text-xs leading-relaxed">
                    L'histoire est validée : TeckiA a préparé <b className="text-white">les acteurs</b> (images générées par TeckiA Image Studio), <b className="text-white">les prompts de chaque scène</b>,
                    le <b className="text-white">modèle vidéo</b> choisi pour chacune et le <b className="text-white">prix total du film</b>. Ouvrez chaque scène, vérifiez son prompt puis validez-la : les scènes nouvelles partent en même temps, les suites attendent la scène d'avant. Rien n'est facturé avant le lancement.
                  </p>
                  <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                    {[['🎭', 'Acteurs', formatAr(filmLiveCharsCost)], ['🎬', 'Scènes', formatAr(filmLiveScenesCost)], ['💰', 'Total', formatAr(filmLiveTotal)]].map(([i, l, v]) => (
                      <div key={l} className="rounded-xl bg-black/25 border border-white/10 py-2"><div className="text-sm">{i}</div><div className="text-[10px] text-gray-400">{l}</div><div className="text-xs font-bold text-white">{v}</div></div>
                    ))}
                  </div>
                </div>

                {filmScript.characters?.length > 0 && (
                  <div>
                    <h3 className="text-white font-bold text-sm mb-2.5 flex items-center gap-1.5"><Users className="w-4 h-4 text-purple-300" /> Personnages</h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {filmScript.characters.map((c, i) => (
                        <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-3.5">
                          {(() => {
                            const mode = c.image_mode || (c.image_url ? 'upload' : 'generate');
                            return (
                              <>
                                <div className="text-white text-xs font-bold mb-2">{c.name}</div>
                                <div className="grid grid-cols-3 gap-1.5 mb-2.5">
                                  {[{ v: 'generate', l: 'Générer', s: formatAr(filmCost?.character_price_unit) }, { v: 'upload', l: 'Ma photo', s: 'gratuit' }, { v: 'none', l: 'Sans image', s: 'texte seul' }].map(o => (
                                    <button
                                      key={o.v}
                                      onClick={() => setCharacterMode(i, o.v)}
                                      className={`py-1.5 rounded-lg text-[10px] font-bold border transition ${mode === o.v ? 'bg-purple-600 border-purple-400 text-white' : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/25'}`}
                                    >
                                      {o.l}
                                      <span className="block text-[8px] font-normal opacity-70">{o.s}</span>
                                    </button>
                                  ))}
                                </div>
                                {mode === 'upload' && (
                                  <div className="flex items-center gap-2.5 mb-2.5">
                                    {c.image_url ? (
                                      <img src={c.image_url} alt={c.name} className="w-14 h-14 rounded-lg object-cover border border-purple-500/30" />
                                    ) : (
                                      <div className="w-14 h-14 rounded-lg border border-dashed border-white/20 flex items-center justify-center text-gray-500"><ImagePlus className="w-5 h-5" /></div>
                                    )}
                                    <label className="cursor-pointer px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-[11px] font-semibold transition">
                                      {c.image_url ? 'Changer la photo' : 'Choisir une photo'}
                                      <input type="file" accept="image/*" className="hidden" onChange={e => { uploadCharacterImage(i, e.target.files?.[0]); e.target.value = ''; }} />
                                    </label>
                                  </div>
                                )}
                                <textarea
                                  value={c.description || ''}
                                  onChange={e => updateCharacterField(i, 'description', e.target.value)}
                                  onBlur={refreshFilmPreview}
                                  rows={4}
                                  className="w-full bg-black/25 border border-white/10 rounded-lg px-2.5 py-2 text-gray-300 text-[11px] leading-relaxed focus:outline-none focus:border-purple-400 resize-y transition"
                                />
                                <p className="text-[9px] text-gray-500 mt-1">
                                  {mode === 'generate' ? "Cette description génère l'image du personnage." : mode === 'upload' ? 'Votre photo sert de référence ; la description reste utile au moteur.' : "Pas d'image : le personnage est décrit en texte dans chaque scène."}
                                </p>
                              </>
                            );
                          })()}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="text-white font-bold text-sm mb-2.5 flex items-center gap-1.5">
                    <Clapperboard className="w-4 h-4 text-purple-300" /> Scènes <span className="text-gray-500 font-normal">({filmScript.scenes.length})</span>
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 mb-3 rounded-xl bg-white/5 border border-white/10 px-3 py-2">
                    <span className="text-[11px] text-gray-300 font-semibold">Explications des scènes :</span>
                    {[['fr', '🇫🇷 Français'], ['mg', '🇲🇬 Malagasy']].map(([k, l]) => (
                      <button key={k} onClick={() => chooseExplainLang(k)} className={`px-2.5 py-1 rounded-full text-[10px] font-bold border transition ${filmExplainLang === k ? 'bg-purple-600 border-purple-400 text-white' : 'bg-white/5 border-white/10 text-gray-300'}`}>{l}</button>))}
                    {filmAiBusy === 'mg' && <span className="text-[10px] text-purple-200 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> traduction en cours…</span>}
                    {filmUndo && <button onClick={undoFilmChange} className="ml-auto px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-400/20 border border-amber-300/40 text-amber-100">↩ Annuler la dernière modification</button>}
                  </div>
                  {filmNotice && <div className="mb-3 text-[11px] text-emerald-100 bg-emerald-500/10 border border-emerald-400/30 rounded-xl px-3 py-2 flex items-start gap-2"><span className="flex-1">{filmNotice}</span><button onClick={() => setFilmNotice('')} className="text-emerald-200/70">✕</button></div>}
                  <FilmFlow
                    scenes={filmScript.scenes}
                    providers={providers}
                    priceOf={(sc) => estimatePrice(providers[sc.engine], sc.resolution, sc.duration, false)}
                    formatAr={formatAr}
                    selected={filmSel}
                    onSelect={(i) => setFilmSel(i)}
                    onToggleValidate={toggleFilmValidate}
                    onToggleChain={toggleFilmChainFlow}
                    onReorder={reorderFilmScenes}
                    reorderable
                  />
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <button onClick={() => setAllFilmValidated(true)} className="px-3 py-2 rounded-xl text-[11px] font-bold text-emerald-100 bg-emerald-600/25 hover:bg-emerald-600/40 border border-emerald-400/40 transition">✔ Tout valider</button>
                    <button onClick={() => setAllFilmValidated(false)} className="px-3 py-2 rounded-xl text-[11px] font-bold text-gray-300 bg-white/5 hover:bg-white/10 border border-white/10 transition">Tout remettre à vérifier</button>
                    <span className="text-[10px] text-gray-500">Cliquez une carte pour ouvrir ses réglages (prompt, prix, qualité, correction par l’IA), glissez-la pour changer l’ordre, puis « Valider » : seules les scènes validées sont lancées.</span>
                  </div>
                </div>

                <div className="bg-black/20 border border-white/10 rounded-xl p-4">
                  <div className="flex items-center justify-between text-xs text-gray-400 mb-1.5">
                    <span>Images de personnages générées ({filmGeneratedCharsCount} × {formatAr(filmCost?.character_price_unit)})</span>
                    <span>{formatAr(filmLiveCharsCost)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-400 mb-2.5">
                    <span>Scènes validées ({filmValidatedCount}/{filmScript.scenes.length})</span>
                    <span>{formatAr(filmLiveScenesCost)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-400 mb-2.5">
                    <span>🎞 Montage professionnel (HyperFrames)</span>
                    <span className="text-emerald-300 font-semibold">Inclus</span>
                  </div>
                  <div className="flex items-center justify-between text-white font-bold text-sm pt-2.5 border-t border-white/10">
                    <span>Prix total du film</span>
                    <span className={credits < filmLiveTotal ? 'text-red-300' : 'text-amber-300'}>{formatAr(filmLiveTotal)}</span>
                  </div>
                </div>

                <label className="flex items-start gap-2.5 text-xs text-gray-200 bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 cursor-pointer">
                  <input type="checkbox" className="mt-0.5" checked={filmScript.music !== false} onChange={e => setFilmScript({ ...filmScript, music: e.target.checked })} />
                  <span><b className="text-white">Musique de fond douce</b> (incluse) — toujours sous les voix, jamais au-dessus. Le film a aussi son ambiance sonore et ses bruitages, transitions fondues, étalonnage unique, carton titre et carton de fin.</span>
                </label>

                <label className="flex items-start gap-2.5 text-xs text-gray-200 bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 cursor-pointer">
                  <input type="checkbox" className="mt-0.5" checked={filmScript.teckia_mark !== false} onChange={e => setFilmScript({ ...filmScript, teckia_mark: e.target.checked })} />
                  <span><b className="text-white">Signature TeckiA</b> — petit logo TeckiA pendant le film, « TeckiA présente » à l&apos;ouverture et la carte « Cette vidéo a été créée par TeckiA » à la fin. <b className="text-white">Décochez</b> pour un film sans aucune mention TeckiA : seulement le titre de votre film, en doré, et « FIN ».</span>
                </label>

                {filmProduceError && (
                  <div className="text-red-300 text-xs bg-red-500/10 border border-red-500/20 rounded-xl px-3.5 py-2.5">{filmProduceError}</div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={resetFilmFlow}
                    className="flex-1 py-3 rounded-xl font-bold text-gray-300 bg-white/5 hover:bg-white/10 border border-white/10 transition"
                  >
                    Recommencer
                  </button>
                  <button
                    onClick={startFilmProduction}
                    disabled={filmProducing || filmValidatedCount === 0}
                    className="flex-[2] py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2 disabled:opacity-40 transition hover:scale-[1.01]"
                    style={{ background: 'linear-gradient(135deg, #9333EA, #DB2777, #9333EA)' }}
                  >
                    {filmProducing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                    {filmProducing ? 'Lancement...' : filmValidatedCount === 0 ? 'Validez au moins une scène' : `Lancer ${filmValidatedCount} scène${filmValidatedCount > 1 ? 's' : ''} validée${filmValidatedCount > 1 ? 's' : ''} · ${formatAr(filmLiveTotal)}`}
                  </button>
                </div>
              </div>
            )}

            {filmStage === 'production' && (
              <div className="space-y-5">
                <div className="relative bg-[#140a24]/95 border border-purple-500/15 rounded-2xl p-6 shadow-[0_0_40px_rgba(147,51,234,0.08)]">
                  <h2 className="text-white font-black text-xl mb-1">{filmStatus?.title || filmScript?.title}</h2>
                  <p className="text-gray-400 text-xs">{filmStatus?.summary}</p>
                </div>

                {!filmStatus && (
                  <div className="flex items-center justify-center gap-2 text-gray-400 text-xs py-6">
                    <Loader2 className="w-4 h-4 animate-spin text-purple-400" /> Préparation de la production...
                  </div>
                )}

                {filmStatus?.stage === 'error' && (
                  <div className="bg-red-500/10 border border-red-500/25 rounded-2xl p-5 text-center">
                    <div className="text-red-300 text-sm font-semibold mb-3">{filmStatus.error || 'La production a échoué.'}</div>
                    {filmStatus.failed_scene && <p className="text-[11px] text-fuchsia-200 mb-3">👇 Corrigez la scène échouée juste en dessous (un bouton l&apos;adoucit pour vous), puis relancez-la : le reste du film est conservé.</p>}
                    <button onClick={resetFilmFlow} className="px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition">Recommencer tout le film</button>
                  </div>
                )}

                {filmStatus?.stage === 'characters' && (
                  <div className="flex items-center justify-center gap-2 text-purple-300 text-xs">
                    <Loader2 className="w-4 h-4 animate-spin" /> Création des personnages en cours (en parallèle)...
                  </div>
                )}

                {filmStatus?.character_images && Object.keys(filmStatus.character_images).length > 0 && (
                  <div>
                    <h3 className="text-white font-bold text-sm mb-2.5 flex items-center gap-1.5"><Users className="w-4 h-4 text-purple-300" /> Personnages</h3>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {Object.entries(filmStatus.character_images).map(([name, url]) => (
                        <div key={name} className="rounded-xl overflow-hidden border border-purple-500/20 aspect-square bg-black/30 relative">
                          {url ? (
                            <img src={url} alt={name} className="w-full h-full object-cover" />
                          ) : filmStatus.stage === 'characters' ? (
                            <div className="w-full h-full flex items-center justify-center"><Loader2 className="w-5 h-5 text-purple-400 animate-spin" /></div>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[10px] text-gray-500 text-center px-2">Image indisponible (décrit en texte)</div>
                          )}
                          <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-center py-1">
                            <span className="text-[10px] font-bold text-white">{name}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {filmStatus?.graph ? renderFlowProduction() : (
                <div>
                  <h3 className="text-white font-bold text-sm mb-2.5 flex items-center gap-1.5">
                    <Clapperboard className="w-4 h-4 text-purple-300" /> Scènes
                    <span className="text-gray-500 font-normal">({(filmStatus?.scenes || []).length}/{filmStatus?.total_planned || filmScript?.scenes.length || 0})</span>
                  </h3>
                  <div className="space-y-3">
                    {(filmStatus?.scenes || []).map((scene, idx) => (
                      <div key={scene.scene_id || idx} className="bg-white/5 border border-white/10 rounded-xl p-3.5">
                        <div className="flex items-center gap-2.5 mb-2">
                          <div
                            className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-black flex-shrink-0"
                            style={{ background: 'linear-gradient(135deg, #9333EA, #DB2777)', boxShadow: '0 0 12px rgba(147,51,234,0.5)' }}
                          >
                            {idx + 1}
                          </div>
                          <span className="text-white font-bold text-xs flex-1">{providers[scene.provider]?.label || scene.provider}</span>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            scene.status === 'completed' ? 'bg-green-500/20 text-green-300'
                            : scene.status === 'failed' ? 'bg-red-500/20 text-red-300'
                            : 'bg-amber-500/20 text-amber-300'
                          }`}>
                            {scene.status === 'completed' ? 'Terminée' : scene.status === 'failed' ? 'Échouée' : 'En cours...'}
                          </span>
                        </div>
                        {idx > 0 && scene.start_image_url && (
                          <div className="flex items-center gap-2.5 mb-2 rounded-lg bg-emerald-500/10 border border-emerald-400/25 p-2">
                            <img src={scene.start_image_url} alt="" className="w-12 h-12 rounded-md object-cover border border-emerald-400/40" />
                            <span className="text-[10px] font-semibold text-emerald-200 leading-snug">🔗 Suite de la scène {idx} : cette scène démarre exactement sur sa dernière image</span>
                          </div>
                        )}
                        <p className="text-gray-300 text-xs mb-2">{scene.prompt_fr || scene.prompt}</p>
                        {scene.status === 'queued' && <TeckiALoader subtitle="Génération de la scène..." />}
                        {scene.status === 'failed' && (
                          <div className="space-y-2.5">
                            <div className="text-red-300 text-xs py-1">{scene.error}</div>
                            {filmStatus?.failed_scene && filmStatus.failed_scene.index === idx && renderFilmFixPanel(filmStatus.failed_scene, fixFilmScene)}
                          </div>
                        )}
                        {scene.status === 'completed' && scene.video_url && (
                          <video src={scene.video_url} controls className="w-full rounded-xl aspect-video object-contain bg-black" />
                        )}
                        {scene.status === 'completed' && scene.last_frame_url && idx < (filmStatus?.total_planned || 0) - 1 && (
                          <div className="flex items-center gap-2.5 mt-2 rounded-lg bg-white/5 border border-white/10 p-2">
                            <img src={scene.last_frame_url} alt="" className="w-12 h-12 rounded-md object-cover border border-white/20" />
                            <span className="text-[10px] text-gray-400 leading-snug">🏁 Dernière image de cette scène — transmise à la scène suivante pour garder la continuité</span>
                          </div>
                        )}
                      </div>
                    ))}
                    {(filmStatus?.scenes || []).length < (filmStatus?.total_planned || 0) && filmStatus?.status !== 'assembled' && (
                      <div className="text-center text-gray-500 text-xs py-2 flex items-center justify-center gap-1.5">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Scène suivante en préparation...
                      </div>
                    )}
                  </div>
                </div>
                )}

                {filmStatus?.status === 'montage' && (
                  <div className="relative bg-[#140a24]/95 border border-amber-400/30 rounded-2xl p-6 text-center shadow-[0_0_40px_rgba(245,185,66,0.12)]">
                    <h3 className="text-white font-bold text-sm mb-2 flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-amber-300" /> Montage professionnel en cours…
                    </h3>
                    <p className="text-gray-300 text-xs leading-relaxed">
                      Toutes vos scènes sont prêtes. TeckiA assemble maintenant votre film : générique d'ouverture, fondus et générique de fin.
                      Cela prend en général 1 à 3 minutes, vous pouvez rester sur cette page.
                    </p>
                  </div>
                )}

                {filmStatus?.status === 'assembled' && filmStatus?.final_video_url && (
                  <div className="relative bg-[#140a24]/95 border border-emerald-500/25 rounded-2xl p-6 text-center shadow-[0_0_40px_rgba(16,185,129,0.12)]">
                    <h3 className="text-white font-bold text-sm mb-3 flex items-center justify-center gap-1.5"><Check className="w-4 h-4 text-emerald-400" /> Votre film est prêt !{filmStatus?.montage === 'done' && <span className="ml-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-400/20 text-amber-200">Montage pro</span>}</h3>
                    <video src={filmStatus.final_video_url} controls className="w-full rounded-xl mb-4" />
                    <div className="flex gap-3 justify-center flex-wrap">
                      <a
                        href={filmStatus.final_video_url} download target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition hover:scale-105"
                      >
                        <Download className="w-4 h-4" /> Télécharger
                      </a>
                      <button
                        onClick={resetFilmFlow}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-sm font-semibold transition hover:scale-105"
                      >
                        <Sparkles className="w-4 h-4" /> Créer un autre film
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {mode === 'pub' && (
          <PubPanel onTopup={() => setShowTopup(true)} onCreditsChange={loadCredits} />
        )}
      </div>

      {renderFilmModal()}

      {showTopup && (
        <TopupModal
          onClose={() => setShowTopup(false)}
          onSubmit={submitTopup}
          submitting={submittingTopup}
          serviceId="ia_studio_video"
          onCardSuccess={handleCardTopupSuccess}
        />
      )}

      {showMyStats && <MyConsumptionStatsModal onClose={() => setShowMyStats(false)} showStoryboards />}

      {trimModal && (
        <VideoTrimModal
          file={trimModal.file}
          maxDuration={trimModal.maxDuration}
          onConfirm={trimModal.onConfirm}
          onCancel={trimModal.onCancel}
        />
      )}
      {audioTrimModal && (
        <AudioTrimModal
          file={audioTrimModal.file}
          maxDuration={audioTrimModal.maxDuration}
          onConfirm={audioTrimModal.onConfirm}
          onCancel={audioTrimModal.onCancel}
        />
      )}
    </div>
  );
}
